import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, BACKEND_URL } from "../App";
import { getRegionNow } from "../utils/regionTime";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft,
  Phone,
  Edit2,
  Save,
  X,
  Loader2,
  User,
  Package,
  FileText,
  Upload,
  ShieldCheck,
  Mail,
  Send,
  Plus,
  TrendingUp,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Linkedin,
  Briefcase,
  Beaker,
  Calendar,
  Pencil,
  Trash2,
  AlertCircle,
  Lock,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { CallRecordingPlayer, RecordingBadge } from "../components/CallRecordingPlayer";

// Import refactored components
import {
  ExistingContactSection,
  FollowupsSection,
  QuickInfoSection,
  DataQualitySection,
  DueFollowupDialog,
  LfbHistorySection,
  COMPOSITION_MATERIALS,
  PERCENTAGE_OPTIONS,
  INDIAN_STATES,
  INDIAN_CITIES,
  BANGLADESH_DIVISIONS,
  BANGLADESH_CITIES,
  DENIM_OZ_OPTIONS,
  DENIER_OPTIONS,
  EPI_PPI_OPTIONS,
  PICK_POOL_BASE_REQUIRED,
  PICK_POOL_DYED_REQUIRED,
  ORDER_TIMELINE_OPTIONS,
} from "./lead-detail";

// Normalize a sample_date value for display. Legacy entries may still contain
// the Excel serial number (e.g. "45875" → 2025-08-08) until the backfill
// endpoint is run; this guarantees the UI always shows a readable date.
const formatSampleDate = (raw) => {
  if (raw === null || raw === undefined) return '—';
  const s = String(raw).trim();
  if (!s) return '—';
  // Already YYYY-MM-DD or starts with one
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // Excel serial date (5-digit number in valid Excel range)
  const num = Number(s);
  if (Number.isFinite(num) && num >= 20000 && num <= 80000) {
    // Excel epoch: 1899-12-30 (accounts for the 1900 leap-year bug)
    const ms = (num * 86400 * 1000) + Date.UTC(1899, 11, 30);
    const d = new Date(ms);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  // Try generic parse (dd/mm/yyyy etc — JS Date.parse is unreliable for these,
  // so prefer dd/mm/yyyy detection)
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const [, a, b, y] = m;
    // Assume day-first (Indian convention)
    return `${y}-${b.padStart(2,'0')}-${a.padStart(2,'0')}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
};

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = ['admin','super_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const isAgent = user?.role === 'user';
  // Raise Data Ticket (agents only) — flags lead-data issues to reviewers
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketReason, setTicketReason] = useState("");
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [isBlocking, setIsBlocking] = useState(false);
  const [ticketComment, setTicketComment] = useState("");
  const [isRaisingTicket, setIsRaisingTicket] = useState(false);
  const [leadTickets, setLeadTickets] = useState([]);
  const [resolvingTicketId, setResolvingTicketId] = useState(null);
  // Order edit / delete (super_admin only)
  const [orderEditing, setOrderEditing] = useState(null);
  const [orderDeleting, setOrderDeleting] = useState(null);
  const [isOrderSaving, setIsOrderSaving] = useState(false);
  const [lead, setLead] = useState(null);
  const [calls, setCalls] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [options, setOptions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPushingToERP, setIsPushingToERP] = useState(false);
  const [isUploadingSample, setIsUploadingSample] = useState(false);
  const [editData, setEditData] = useState({});
  const [showFollowupDialog, setShowFollowupDialog] = useState(false);
  const [showSecondaryContact, setShowSecondaryContact] = useState(false);
  // Bangladesh Swatch/Yardage dispatch dialog
  const [bdDispatch, setBdDispatch] = useState({ open: false, reqId: null, statusType: null, paid: "free", mode: "hand", awb: "" });

  // Derived Samples: union of canonical lead.samples[] with samples inferred from
  // requirements flagged sample_shared / paid_sample_shared / requirement_status ∈
  // ('yardage_sent','sample_shared'). Deduped by req_id — existing lead.samples[]
  // takes priority. This is what powers the Samples tab and keeps legacy leads
  // self-healing without a DB migration.
  const derivedSamples = useMemo(() => {
    const existing = lead?.samples || [];
    const existingReqIds = new Set(existing.map(s => s?.req_id).filter(Boolean));
    const derived = (lead?.requirements || [])
      .filter(r => {
        const indicated = r?.sample_shared === true
          || r?.paid_sample_shared === true
          || ['yardage_sent', 'sample_shared'].includes(r?.requirement_status);
        return indicated && r?.req_id && !existingReqIds.has(r.req_id);
      })
      .map(r => ({
        id: `req-${r.req_id}`,
        req_id: r.req_id,
        product_name: [r?.fabric_type, r?.material_1].filter(Boolean).join(' / ') || 'Sample',
        lfb_id: r?.lfb_id,
        sample_type: r?.sample_type,
        sample_awb: r?.sample_awb,
        sample_date: r?.yardage_sent_at || r?.sample_shared_at || r?.updated_at || r?.created_at || '',
        source: 'requirement',
        created_by_name: null,
      }));
    return [...existing, ...derived];
  }, [lead?.samples, lead?.requirements]);
  const [newFollowup, setNewFollowup] = useState({
    description: "",
    scheduled_at: new Date(),
    priority: "medium",
    outcome: "",
    outcome_notes: "",
    lfb_id: ""
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [dueFollowup, setDueFollowup] = useState(null);
  const [showDueFollowupPopup, setShowDueFollowupPopup] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleTime, setRescheduleTime] = useState("10:00");
  const [pickPoolStatus, setPickPoolStatus] = useState(null);
  const [isPushingToPickPool, setIsPushingToPickPool] = useState(false);
  const [showPickPoolError, setShowPickPoolError] = useState(false);

  // GST Verify state
  const [gstVerifying, setGstVerifying] = useState(false);
  const [gstVerified, setGstVerified] = useState(null);

  // Email state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', subject: '', body: '' });
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [notesSaveStatus, setNotesSaveStatus] = useState("");
  const notesTimerRef = useRef(null);

  // Customer requirements state (lives in lead.requirements array)
  const [showNewRequirementDialog, setShowNewRequirementDialog] = useState(false);
  const [sampleDialog, setSampleDialog] = useState({ open: false, reqId: null, sample_type: 'free', sample_awb: '', current: null });
  const [newRequirement, setNewRequirement] = useState({});
  const [isCreatingRequirement, setIsCreatingRequirement] = useState(false);
  const [pushingReqId, setPushingReqId] = useState(null);
  const [lfbIdInput, setLfbIdInput] = useState("");
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [isSavingRequirement, setIsSavingRequirement] = useState(false);

  // Order edit / delete handlers (super_admin only)
  const ORDER_STATUSES = ['Confirmed', 'Processing', 'Shipped', 'Out for delivery', 'Delivered', 'Cancelled'];
  const openOrderEdit = (o) => {
    setOrderEditing({
      order_slot_id: o.id,
      order_lead_id: o.lead_id || "",
      fabric_type: o.fabric_type || "",
      composition: o.composition || "",
      order_date: o.order_date || "",
      quantity: o.quantity ?? "",
      order_status: o.order_status || "",
      invoice_amount: o.invoice_amount ?? "",
    });
  };
  const saveOrderEdit = async () => {
    if (!orderEditing) return;
    setIsOrderSaving(true);
    try {
      const payload = {
        order_lead_id: orderEditing.order_lead_id,
        fabric_type: orderEditing.fabric_type,
        composition: orderEditing.composition,
        order_date: orderEditing.order_date,
        quantity: orderEditing.quantity === "" ? null : orderEditing.quantity,
        order_status: orderEditing.order_status,
        invoice_amount: orderEditing.invoice_amount === "" ? null : orderEditing.invoice_amount,
      };
      const { data } = await axios.patch(`${API}/orders/${id}/${orderEditing.order_slot_id}`, payload);
      const changed = Object.keys(data.changes || {}).length;
      toast.success(changed ? `Order updated (${changed} field${changed !== 1 ? "s" : ""})` : "Order saved — no changes");
      setOrderEditing(null);
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update order");
    } finally {
      setIsOrderSaving(false);
    }
  };
  const confirmOrderDelete = async () => {
    if (!orderDeleting) return;
    setIsOrderSaving(true);
    try {
      await axios.delete(`${API}/orders/${id}/${orderDeleting.order_slot_id}`);
      toast.success(`Order ${orderDeleting.order_lead_id || ''} deleted`);
      setOrderDeleting(null);
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete order");
    } finally {
      setIsOrderSaving(false);
    }
  };

  const fetchLeadData = async () => {
    try {
      const [leadRes, callsRes, followupsRes, optionsRes] = await Promise.all([
        axios.get(`${API}/leads/${id}`),
        axios.get(`${API}/calls?lead_id=${id}`),
        axios.get(`${API}/followups?lead_id=${id}`),
        axios.get(`${API}/categories/all-options`)
      ]);
      setLead(leadRes.data);
      setEditData(leadRes.data);
      // Auto-expand secondary contact if data exists
      if (leadRes.data?.contact_2_name || leadRes.data?.contact_2_phone || leadRes.data?.contact_2_email) {
        setShowSecondaryContact(true);
      }
      setCalls(callsRes.data);
      setFollowups(followupsRes.data);
      setOptions(optionsRes.data);
      // Fetch ICP score breakdown
      try {
        const scoreRes = await axios.get(`${API}/leads/${id}/score`);
        setScoreBreakdown(scoreRes.data);
      } catch {}
      // Fetch open + resolved tickets for this lead
      try {
        const t = await axios.get(`${API}/leads/${id}/tickets`);
        setLeadTickets(t.data.tickets || []);
      } catch {}
    } catch (error) {
      console.error("Failed to fetch lead:", error);
      toast.error("Failed to load lead details");
      navigate("/leads");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadData();
  }, [id]);

  // Check Pick Pool status when lead loads
  useEffect(() => {
    if (lead?.id) {
      checkPickPoolStatus();
      fetchEmailHistory();
    }
  }, [lead?.id]);

  const checkPickPoolStatus = async () => {
    try {
      const response = await axios.get(`${API}/pick-pool/check/${id}`);
      setPickPoolStatus(response.data);
    } catch (error) {
      console.error("Failed to check pick pool status:", error);
    }
  };

  // Fetch email history
  const fetchEmailHistory = async () => {
    try {
      const res = await axios.get(`${API}/leads/${id}/emails`);
      setEmailHistory(res.data);
    } catch (err) {
      console.error("Failed to fetch email history:", err);
    }
  };

  // Fetch customer requirements (all leads for same customer)
  // Requirements now live in lead.requirements array — no separate fetch needed

  // Create new requirement (adds to lead's requirements array)
  const handleCreateRequirement = async () => {
    setIsCreatingRequirement(true);
    try {
      await axios.post(`${API}/leads/${id}/requirements`, newRequirement);
      toast.success("New requirement added");
      setShowNewRequirementDialog(false);
      setNewRequirement({});
      fetchLeadData(); // Refresh lead to see the new requirement
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add requirement");
    } finally {
      setIsCreatingRequirement(false);
    }
  };

  // Push a specific requirement to pick pool. LFB is now OPTIONAL — if blank,
  // the requirement lands in `in_pick_pool` and the agent can submit LFB later.
  const handlePushRequirementToPickPool = async (reqId) => {
    try {
      const body = lfbIdInput.trim() ? { lfb_id: lfbIdInput.trim() } : {};
      const r = await axios.post(`${API}/leads/${id}/requirements/${reqId}/push-to-pickpool`, body);
      toast.success(r.data?.message || "Pushed to Pick Pool");
      setPushingReqId(null);
      setLfbIdInput("");
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to push to pick pool");
    }
  };

  // Stage 2: agent submits the LFB ID after the LFB record has been created.
  const handleSubmitLfbId = async (reqId) => {
    if (!lfbIdInput.trim()) { toast.error("LFB ID is required"); return; }
    try {
      const r = await axios.post(`${API}/leads/${id}/requirements/${reqId}/submit-lfb-id`, { lfb_id: lfbIdInput.trim() });
      toast.success(r.data?.message || "LFB ID attached");
      setPushingReqId(null);
      setLfbIdInput("");
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to attach LFB ID");
    }
  };

  // Update requirement status
  const handleUpdateRequirementStatus = async (reqId, newStatus, extras = {}) => {
    try {
      await axios.patch(`${API}/leads/${id}/requirements/${reqId}`, { requirement_status: newStatus, ...extras });
      toast.success(`Status updated to "${newStatus.replace(/_/g, ' ')}"`);
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    }
  };

  // Manager-only: zero / re-verify a Bangladesh call's integrity status
  const handleSetCallVerification = async (callId, newStatus) => {
    try {
      await axios.patch(`${API}/calls/${callId}/verification`, { verification_status: newStatus });
      toast.success(newStatus === 'zeroed' ? 'Call zeroed — excluded from Health Score' : 'Call re-verified');
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${newStatus === 'zeroed' ? 'zero' : 'verify'} call`);
    }
  };

  // Bangladesh dispatch dialog (Yardage extra capture only — swatch is "serviced locally" per PDF)
  const handleBangladeshStatusChange = (reqId, newStatus) => {
    if (newStatus === "yardage_sent") {
      setBdDispatch({
        open: true, reqId, statusType: newStatus,
        paid: "free", mode: "hand", awb: "",
      });
    } else {
      handleUpdateRequirementStatus(reqId, newStatus);
    }
  };

  const submitBangladeshDispatch = async () => {
    const { reqId, statusType, paid, mode, awb } = bdDispatch;
    if (mode === "courier" && !awb.trim()) {
      toast.error("AWB Number is required when sent by courier");
      return;
    }
    const prefix = "yardage";
    const extras = {
      [`${prefix}_paid`]: paid,
      [`${prefix}_mode`]: mode,
      [`${prefix}_awb`]: mode === "courier" ? awb.trim() : "",
    };
    await handleUpdateRequirementStatus(reqId, statusType, extras);
    setBdDispatch({ open: false, reqId: null, statusType: null, paid: "free", mode: "hand", awb: "" });
  };

  // Save edited requirement
  const handleSaveRequirement = async () => {
    if (!editingRequirement) return;
    setIsSavingRequirement(true);
    try {
      await axios.patch(`${API}/leads/${id}/requirements/${editingRequirement.req_id}`, editingRequirement);
      toast.success("Requirement updated");
      setEditingRequirement(null);
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update requirement");
    } finally {
      setIsSavingRequirement(false);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    setIsSendingEmail(true);
    try {
      await axios.post(`${API}/leads/${id}/send-email`, {
        to: emailForm.to || lead.email,
        subject: emailForm.subject,
        body: emailForm.body,
      });
      toast.success("Email sent successfully");
      setShowEmailDialog(false);
      setEmailForm({ to: '', subject: '', body: '' });
      fetchEmailHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // GST Verify handler for Edit Lead
  const handleVerifyGST = async () => {
    const gstin = (editData.gst_number || "").trim();
    if (!gstin || gstin.length !== 15) {
      toast.error("Enter a valid 15-character GSTIN");
      return;
    }
    setGstVerifying(true);
    setGstVerified(null);
    try {
      const res = await axios.post(`${API}/gst/lookup`, { gstin });
      const { mapped_fields, gst_status, is_active, raw_info } = res.data;
      if (!is_active) {
        toast.error(`GST is ${gst_status}. Cannot auto-fill details for inactive GST.`);
        setGstVerified({ status: gst_status, active: false });
        setGstVerifying(false);
        return;
      }
      setEditData(prev => ({
        ...prev,
        company_name: mapped_fields.company_name || prev.company_name,
        business_type: mapped_fields.business_type || prev.business_type,
        gst_registration_date: mapped_fields.gst_registration_date || prev.gst_registration_date,
        city: mapped_fields.city || prev.city,
        state: mapped_fields.state || prev.state,
        address: mapped_fields.address || prev.address,
      }));
      setGstVerified({ status: gst_status, active: true, constitution: raw_info.constitution });
      toast.success(`GST Verified — ${mapped_fields.company_name}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "GST lookup failed";
      toast.error(msg);
      setGstVerified(null);
    } finally {
      setGstVerifying(false);
    }
  };

  const formatFieldName = (field) => {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const handlePushToPickPool = async () => {
    setIsPushingToPickPool(true);
    try {
      const response = await axios.get(`${API}/pick-pool/check/${id}`);
      setPickPoolStatus(response.data);
      
      if (response.data.is_pick_pool_ready) {
        await axios.patch(`${API}/leads/${id}`, { pick_pool_ready: true });
        toast.success("Lead pushed to Pick Pool successfully!");
        fetchLeadData();
      } else {
        setShowPickPoolError(true);
        // Compose a precise reason instead of the misleading "Missing 0 mandatory fields"
        const missingCount = response.data.missing_count || 0;
        const parts = [];
        if (missingCount > 0) parts.push(`Missing ${missingCount} mandatory field${missingCount === 1 ? '' : 's'}`);
        if (response.data.name_company_same) parts.push("Contact Person and Company Name are identical");
        if ((response.data.call_log_count ?? 1) === 0) parts.push("At least 1 call log required");
        toast.error(parts.length ? parts.join(' · ') : "Cannot push to Pick Pool — see requirements below");
      }
    } catch (error) {
      console.error("Failed to push to pick pool:", error);
      toast.error("Failed to check pick pool eligibility");
    } finally {
      setIsPushingToPickPool(false);
    }
  };

  const handleBlockLead = async () => {
    // Admin action — soft-remove the lead. Backend clears assigned_to and
    // stamps blocked_reason/blocked_at/blocked_by so we keep an audit trail
    // instead of deleting the record outright.
    if (!blockReason.trim()) { toast.error("Reason is required"); return; }
    setIsBlocking(true);
    try {
      await axios.post(`${API}/leads/${id}/block`, { reason: blockReason.trim() });
      toast.success("Lead blocked");
      setShowBlockDialog(false);
      setBlockReason("");
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Block failed");
    } finally {
      setIsBlocking(false);
    }
  };

  const handleUnblockLead = async () => {
    if (!window.confirm("Unblock this lead? It becomes reusable for allocation.")) return;
    try {
      await axios.post(`${API}/leads/${id}/unblock`);
      toast.success("Lead unblocked");
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unblock failed");
    }
  };


  const handleReRequestSample = async (sampleId) => {
    // AM or admin can turn a rejected sample into a fresh pending one. The
    // backend clones the LFB linkage and stamps `previous_sample_id` so the
    // rejection history stays visible.
    if (!window.confirm("Create a fresh sample under the same LFB? The rejected record stays for history.")) return;
    try {
      await axios.post(`${API}/samples/${sampleId}/re-request`);
      toast.success("Fresh sample created");
      fetchLeadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Re-request failed");
    }
  };

  const handleUploadSample = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, PNG, WEBP allowed.");
      return;
    }

    setIsUploadingSample(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/leads/${id}/upload-sample`, formData);
      toast.success("Sample image uploaded successfully");
      const newImageUrl = response.data.image_url;
      setLead(prev => ({ ...prev, sample_shared: true, sample_image_url: newImageUrl }));
      setEditData(prev => ({ ...prev, sample_shared: true, sample_image_url: newImageUrl }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload sample image");
    } finally {
      setIsUploadingSample(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Filter out computed/read-only fields before sending
      const excludeFields = ['id', '_id', 'icp_score', 'last_call_status', 'last_call_at', 'brand_detection', 'created_at', 'updated_at', 'call_count', 'last_touched_at', 'auto_allocated', 'auto_allocated_at', 'pushed_to_pick_pool_by_id'];
      const payload = Object.fromEntries(
        Object.entries(editData).filter(([key]) => !excludeFields.includes(key))
      );
      const res = await axios.patch(`${API}/leads/${id}`, payload);
      setLead(editData);
      setIsEditing(false);
      toast.success("Lead updated successfully");

      // ERP push result (Denim + India)
      const erp = res?.data?.erp;
      if (erp) {
        if (erp.success) {
          const msg = erp.deduplicated
            ? `ERP RFQ already exists: ${erp.rfq_number}`
            : `Pushed to ERP — RFQ ${erp.rfq_number}`;
          toast.success(msg, { duration: 6000 });
        } else {
          toast.error(`ERP push failed: ${erp.error || "unknown error"}`, { duration: 10000 });
        }
      }
      // Refresh lead so erp_rfq_number / erp_last_error appear in header badges
      fetchLeadData();
    } catch (error) {
      // FastAPI 422 returns `detail` as an array of objects — passing that to
      // `toast.error` would crash the render tree (React can't render an object
      // as a child) and leave the page blank. Coerce to a readable string.
      const raw = error?.response?.data?.detail;
      let msg = "Failed to update lead";
      if (typeof raw === "string") msg = raw;
      else if (Array.isArray(raw) && raw.length) {
        msg = raw
          .map((e) => `${(e.loc || []).join(".")}: ${e.msg || "invalid"}`)
          .join(" · ");
      } else if (raw && typeof raw === "object") {
        try { msg = JSON.stringify(raw); } catch { /* keep default */ }
      }
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClickToCall = async () => {
    try {
      const response = await axios.post(`${API}/calls/click-to-call`, {
        lead_id: lead.id,
        phone_number: lead.phone
      });
      toast.success(response.data.message);
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate call");
    }
  };

  const [showManualCallDialog, setShowManualCallDialog] = useState(false);
  const [showCallChoiceDialog, setShowCallChoiceDialog] = useState(false);
  // Helper: derive a default follow-up date = call_date + 7 days
  const addDays = (yyyy_mm_dd, days) => {
    if (!yyyy_mm_dd) return '';
    const d = new Date(yyyy_mm_dd + 'T00:00:00');
    if (isNaN(d.getTime())) return '';
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  };
  // Region-local "now" — India leads see IST, Bangladesh leads see BDT.
  // Call date/time are FROZEN to this value (read-only) so the log always
  // reflects the actual moment the call is being recorded.
  const leadRegion = lead?.region;
  const _initialRegionNow = getRegionNow(leadRegion);
  const _initialCallDate = _initialRegionNow.date;
  const [manualCallForm, setManualCallForm] = useState({
    call_date: _initialCallDate,
    call_time: _initialRegionNow.time,
    tz_label: _initialRegionNow.label,
    tz_zone: _initialRegionNow.zone,
    status: "connected",
    duration_minutes: "",
    duration_seconds_input: "",
    notes: "",
    // Mandatory follow-up (auto-default to call_date + 7 days)
    followup_description: "",
    followup_date: addDays(_initialCallDate, 7),
    followup_time: "10:00",
    followup_priority: "medium",
    followup_date_touched: false,  // tracks if user manually edited; if false we re-derive on call_date change
  });

  // Keep call_date/call_time refreshed to the lead's region-local now while
  // the dialog is open. Ticks every 20s and also immediately when the dialog
  // is opened or the lead's region loads.
  useEffect(() => {
    if (!showManualCallDialog) return;
    const refresh = () => {
      const n = getRegionNow(leadRegion);
      setManualCallForm(prev => ({
        ...prev,
        call_date: n.date,
        call_time: n.time,
        tz_label: n.label,
        tz_zone: n.zone,
        followup_date: prev.followup_date_touched ? prev.followup_date : addDays(n.date, 7),
      }));
    };
    refresh();
    const id = setInterval(refresh, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManualCallDialog, leadRegion]);
  const [callScreenshot, setCallScreenshot] = useState(null);
  const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
  const [isSubmittingManualCall, setIsSubmittingManualCall] = useState(false);

  const handleManualCallSubmit = async () => {
    // Guard against double-click / double submit
    if (isSubmittingManualCall) return;
    // Validate: connected calls must have duration > 0
    const connectedStatuses = ["connected", "requirement_shared"];
    if (connectedStatuses.includes(manualCallForm.status)) {
      const totalSec = (parseInt(manualCallForm.duration_minutes) || 0) * 60 + (parseInt(manualCallForm.duration_seconds_input) || 0);
      if (totalSec === 0) {
        toast.error("Duration cannot be zero for connected calls");
        return;
      }
    }
    // Mandatory follow-up for agents (role=user). Admin/super_admin can skip.
    // — Disabled per latest product call: follow-up description is now optional for everyone.
    // const isAgent = user?.role === 'user';
    // if (isAgent && (!manualCallForm.followup_description || manualCallForm.followup_description.trim().length < 3)) {
    //   toast.error("A follow-up is mandatory when logging a call. Please add a description.");
    //   return;
    // }
    setIsSubmittingManualCall(true);
    try {
      const response = await axios.post(`${API}/calls/manual-log`, {
        lead_id: lead.id,
        ...manualCallForm
      });
      const callId = response.data.call_id;

      // Upload screenshot if selected
      if (callScreenshot && callId) {
        setIsUploadingScreenshot(true);
        const formData = new FormData();
        formData.append('file', callScreenshot);
        try {
          await axios.post(`${API}/calls/${callId}/upload-screenshot`, formData);
        } catch (uploadErr) {
          console.error("Screenshot upload failed:", uploadErr);
          toast.warning("Call logged but screenshot upload failed");
        } finally {
          setIsUploadingScreenshot(false);
        }
      }

      toast.success(response.data?.followup_id ? "Call logged + follow-up scheduled" : "Call logged successfully");
      setShowManualCallDialog(false);
      setCallScreenshot(null);
      const _resetNow = getRegionNow(leadRegion);
      setManualCallForm({
        call_date: _resetNow.date,
        call_time: _resetNow.time,
        tz_label: _resetNow.label,
        tz_zone: _resetNow.zone,
        status: "connected",
        duration_minutes: "",
        duration_seconds_input: "",
        notes: "",
        followup_description: "",
        followup_date: addDays(_resetNow.date, 7),
        followup_time: "10:00",
        followup_priority: "medium",
        followup_date_touched: false,
      });
      fetchLeadData();
    } catch (error) {
      console.error("Manual call log failed:", error);
      const d = error.response?.data?.detail;
      let msg;
      if (typeof d === "string") {
        msg = d;
      } else if (Array.isArray(d)) {
        // FastAPI validation error
        msg = d.map(e => `${(e.loc || []).join('.')}: ${e.msg}`).join('; ');
      } else if (d && typeof d === "object") {
        msg = d.msg || JSON.stringify(d);
      } else if (error.response?.data?.message) {
        msg = error.response.data.message;
      } else if (error.response?.status) {
        msg = `Failed to log call (HTTP ${error.response.status} ${error.response.statusText || ""})`;
      } else if (error.message) {
        msg = `Failed to log call: ${error.message}`;
      } else {
        msg = "Failed to log call";
      }
      toast.error(msg, { duration: 6000 });
    } finally {
      setIsSubmittingManualCall(false);
    }
  };

  const handleCreateFollowup = async (e) => {
    e.preventDefault();
    try {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
      
      await axios.post(`${API}/followups`, {
        ...newFollowup,
        lead_id: id,
        scheduled_at: scheduledDateTime.toISOString()
      });
      toast.success("Follow-up scheduled");
      setShowFollowupDialog(false);
      setNewFollowup({ description: "", scheduled_at: new Date(), priority: "medium", outcome: "", outcome_notes: "", lfb_id: "" });
      setSelectedTime("10:00");
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to create follow-up");
    }
  };

  const handleCompleteFollowup = async (followupId) => {
    try {
      await axios.patch(`${API}/followups/${followupId}`, { completed: true });
      toast.success("Follow-up marked as complete");
      setShowDueFollowupPopup(false);
      setDueFollowup(null);
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to update follow-up");
    }
  };

  const handleRescheduleFollowup = async () => {
    if (!dueFollowup) return;
    try {
      const [hours, minutes] = rescheduleTime.split(':').map(Number);
      const newDateTime = new Date(rescheduleDate);
      newDateTime.setHours(hours, minutes, 0, 0);
      
      await axios.patch(`${API}/followups/${dueFollowup.id}`, {
        scheduled_at: newDateTime.toISOString()
      });
      toast.success("Follow-up rescheduled");
      setShowDueFollowupPopup(false);
      setDueFollowup(null);
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to reschedule follow-up");
    }
  };

  // Check for due follow-ups
  useEffect(() => {
    const checkDueFollowups = () => {
      if (showDueFollowupPopup) return;
      
      const now = new Date();
      const dueFollowups = followups.filter(f => {
        if (f.completed) return false;
        const scheduledTime = new Date(f.scheduled_at);
        return scheduledTime <= new Date(now.getTime() + 60000);
      });
      
      if (dueFollowups.length > 0) {
        const nextDue = dueFollowups[0];
        if (!dueFollowup || dueFollowup.id !== nextDue.id) {
          setDueFollowup(nextDue);
          setShowDueFollowupPopup(true);
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          setRescheduleDate(tomorrow);
          const scheduledTime = new Date(nextDue.scheduled_at);
          setRescheduleTime(scheduledTime.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }));
        }
      }
    };
    
    checkDueFollowups();
    const interval = setInterval(checkDueFollowups, 30000);
    return () => clearInterval(interval);
  }, [followups, showDueFollowupPopup, dueFollowup]);

  const handlePushToERP = async () => {
    if (!window.confirm("Push this lead to ERP? This will send all lead details to your internal system.")) {
      return;
    }
    
    setIsPushingToERP(true);
    try {
      const response = await axios.post(`${API}/leads/${id}/push-to-erp`);
      toast.success(response.data.message || "Lead pushed to ERP successfully!");
      fetchLeadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to push lead to ERP";
      toast.error(errorMsg);
    } finally {
      setIsPushingToERP(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: "bg-blue-50 text-blue-700 border-blue-200",
      contacted: "bg-yellow-50 text-yellow-700 border-yellow-200",
      contacted_lfb: "bg-emerald-50 text-emerald-700 border-emerald-200",
      qualified: "bg-purple-50 text-purple-700 border-purple-200",
      converted_to_lfb: "bg-emerald-50 text-emerald-700 border-emerald-200",
      lost: "bg-red-50 text-red-700 border-red-200"
    };
    return colors[status] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  const getComputedStatus = (lead) => {
    if (!lead) return { value: "new", label: "New", description: "No calls made yet" };
    
    const hasLfb = lead.lfb_created === true;
    const hasCallsMade = (lead.call_count && lead.call_count > 0) || lead.last_call_date;
    
    if (hasLfb) return { value: "contacted_lfb", label: "Contacted & LFB", description: "Call made + LFB created" };
    if (hasCallsMade) return { value: "contacted", label: "Contacted", description: "Call has been made" };
    return { value: "new", label: "New", description: "No calls made yet" };
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: "bg-red-50 text-red-700",
      medium: "bg-yellow-50 text-yellow-700",
      low: "bg-slate-50 text-slate-600"
    };
    return colors[priority] || "bg-slate-50 text-slate-600";
  };

  const isPickPoolRequired = (field) => {
    if (PICK_POOL_BASE_REQUIRED.includes(field)) return true;
    if (PICK_POOL_DYED_REQUIRED.includes(field)) {
      return editData.fabric_type === 'Dyed';
    }
    return false;
  };

  const renderSelect = (field, label, optionsList, placeholder = "Select...", { required = false, pickPoolRequired = false } = {}) => (
    <div className="space-y-2">
      <Label className="text-slate-500 text-sm">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {pickPoolRequired && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {isEditing ? (
        <Select
          value={editData[field] || ""}
          onValueChange={(value) => {
            const updates = { [field]: value };
            // Auto-set quantity unit based on fabric category
            if (field === 'fabric_category') {
              if (value === 'Woven') updates.quantity_unit = 'Meters';
              else if (value === 'Knits') updates.quantity_unit = 'Kgs';
            }
            setEditData(prev => ({ ...prev, ...updates }));
          }}
        >
          <SelectTrigger data-testid={`edit-${field}-select`} className="h-9">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {optionsList?.map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-slate-900 text-sm">{lead[field] || "-"}</p>
      )}
    </div>
  );

  const renderInput = (field, label, placeholder = "", { required = false, pickPoolRequired = false, type = "text", min, max, step } = {}) => (
    <div className="space-y-2">
      <Label className="text-slate-500 text-sm">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {pickPoolRequired && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {isEditing ? (
        <Input
          type={type}
          {...(min !== undefined ? { min } : {})}
          {...(max !== undefined ? { max } : {})}
          {...(step !== undefined ? { step } : {})}
          value={editData[field] ?? ""}
          onChange={(e) => {
            let value = e.target.value;
            // Phone fields: block comma/semicolon/slash-separated numbers; keep at most one leading '+'
            if (field === "phone" || field === "contact_2_phone") {
              const original = value;
              value = value.replace(/[,;/\n\r|&]/g, "");
              if (value.indexOf("+") > 0) value = value.replace(/\+/g, "");
              if (value !== original) {
                toast.warning("Only one number per field. Use the secondary contact for another number.");
              }
            }
            setEditData(prev => ({ ...prev, [field]: value }));
          }}
          placeholder={placeholder}
          className="h-9"
          data-testid={`edit-${field}-input`}
        />
      ) : (
        <p className="text-slate-900 text-sm">{lead[field] ?? "-"}</p>
      )}
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!lead || !options) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Lead not found</p>
        <Link to="/leads" className="text-blue-600 hover:underline mt-2 inline-block">Back to Leads</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/leads")} className="text-slate-500" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {lead.name}
            </h1>
            <p className="text-slate-500">{lead.company_name}</p>
          </div>
          <span className={`${getStatusColor(getComputedStatus(lead).value)} px-2 py-1 rounded text-sm font-medium border`}>
            {getComputedStatus(lead).label}
          </span>
          {lead.is_new && (
            <span
              className="bg-blue-100 text-blue-700 border border-blue-300 px-2 py-1 rounded text-xs font-bold uppercase tracking-wide"
              data-testid="new-lead-badge"
              title="New lead assigned to you — badge clears when you log your first call"
            >
              New
            </span>
          )}
          {lead.is_blocked && (
            <span
              className="bg-slate-800 text-white border border-slate-800 px-2 py-1 rounded text-xs font-bold uppercase tracking-wide"
              data-testid="lead-blocked-badge"
              title={lead.blocked_reason ? `Blocked: ${lead.blocked_reason}` : "Blocked by admin"}
            >
              Blocked
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => { setIsEditing(false); setEditData(lead); }} data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-2" />Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-lead-btn">
                {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-lead-btn">
                <Edit2 className="w-4 h-4 mr-2" />Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => { setTicketReason(""); setTicketComment(""); setTicketOpen(true); }}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                data-testid="raise-ticket-btn"
              >
                <AlertCircle className="w-4 h-4 mr-2" />Raise Ticket
              </Button>
              {lead.region === "Bangladesh" ? (
                <Button onClick={() => setShowManualCallDialog(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="log-call-btn">
                  <Phone className="w-4 h-4 mr-2" />Log Call
                </Button>
              ) : (
                <Button onClick={() => setShowCallChoiceDialog(true)} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="call-lead-btn">
                  <Phone className="w-4 h-4 mr-2" />Call
                </Button>
              )}
              {lead.email && (
                <Button
                  onClick={() => {
                    setEmailForm({ to: lead.email, subject: '', body: '' });
                    setShowEmailDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="send-email-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />Send Email
                </Button>
              )}
              {isAdmin && (
                lead.is_blocked ? (
                  <Button
                    variant="outline"
                    onClick={handleUnblockLead}
                    className="border-slate-400 text-slate-700 hover:bg-slate-100"
                    data-testid="unblock-lead-btn"
                    title={lead.blocked_reason ? `Blocked reason: ${lead.blocked_reason}` : ""}
                  >
                    Unblock
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => { setBlockReason(""); setShowBlockDialog(true); }}
                    className="border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white"
                    data-testid="block-lead-btn"
                    title="Soft-remove this lead — stays in the CRM with a Blocked pill; auto-unassigns"
                  >
                    Block
                  </Button>
                )
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          {/* Order Timeline - Prominent Section (Required for Pick Pool) */}
          {!isAdmin && (
            <Card className={`border-2 mb-4 ${editData.order_timeline ? 'border-emerald-200 bg-emerald-50/30' : 'border-orange-300 bg-orange-50'}`}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <span className="text-orange-600">*</span>
                      When is the customer looking to place this order?
                      <span className="text-xs font-normal text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Required for Pick Pool</span>
                    </Label>
                    <p className="text-xs text-slate-500 mt-1">This helps prioritize leads based on urgency</p>
                  </div>
                  <div className="w-full sm:w-64">
                    {isEditing ? (
                      <Select
                        value={editData.order_timeline || ""}
                        onValueChange={(value) => setEditData(prev => ({ ...prev, order_timeline: value }))}
                      >
                        <SelectTrigger 
                          data-testid="order-timeline-select" 
                          className={`h-10 ${!editData.order_timeline ? 'border-orange-400 ring-2 ring-orange-100' : 'border-emerald-400'}`}
                        >
                          <SelectValue placeholder="Select timeline..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ORDER_TIMELINE_OPTIONS.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        lead.order_timeline 
                          ? lead.order_timeline === 'Within a week' 
                            ? 'bg-red-100 text-red-700 border border-red-200'
                            : lead.order_timeline === 'Within 15 days'
                              ? 'bg-orange-100 text-orange-700 border border-orange-200'
                              : lead.order_timeline === 'Within 30 days'
                                ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                                : 'bg-slate-100 text-slate-600 border border-slate-200'
                          : 'bg-slate-100 text-slate-400 border border-slate-200'
                      }`}>
                        {lead.order_timeline || "Not set - Click Edit to select"}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Admin view - read only */}
          {isAdmin && lead.order_timeline && (
            <Card className="border-emerald-200 bg-emerald-50/30 mb-4">
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <Label className="text-sm font-semibold text-slate-700">Order Timeline:</Label>
                  <div className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    lead.order_timeline === 'Within a week' 
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : lead.order_timeline === 'Within 15 days'
                        ? 'bg-orange-100 text-orange-700 border border-orange-200'
                        : lead.order_timeline === 'Within 30 days'
                          ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                          : 'bg-slate-100 text-slate-600 border border-slate-200'
                  }`}>
                    {lead.order_timeline}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Open data-quality tickets — dismissable by admin/super-admin only */}
          {(() => {
            const openTickets = (leadTickets || []).filter(t => (t.status || 'open') !== 'resolved');
            if (openTickets.length === 0) return null;
            return (
              <div className="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 px-4 py-3" data-testid="open-tickets-banner">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-red-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {openTickets.length} open data-quality {openTickets.length === 1 ? 'ticket' : 'tickets'} on this lead
                  </p>
                </div>
                <div className="space-y-2">
                  {openTickets.map(t => (
                    <div key={t.id} className="flex items-start justify-between gap-3 bg-white/70 rounded p-2 border border-red-100" data-testid={`open-ticket-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <span className="font-medium text-red-900">{t.reason}</span>
                          {t.comment && <span className="text-slate-600"> — {t.comment}</span>}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Raised by {t.raised_by_name || t.raised_by_email} · {t.created_at ? new Date(t.created_at).toLocaleString() : '—'}
                        </p>
                      </div>
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={resolvingTicketId === t.id}
                          onClick={async () => {
                            setResolvingTicketId(t.id);
                            try {
                              await axios.patch(`${API}/lead-tickets/${t.id}/resolve`, {});
                              toast.success("Ticket resolved");
                              const r = await axios.get(`${API}/leads/${id}/tickets`);
                              setLeadTickets(r.data.tickets || []);
                              const openLeft = (r.data.tickets || []).filter(x => (x.status || 'open') !== 'resolved').length;
                              if (openLeft === 0) {
                                setLead(prev => prev ? { ...prev, has_open_ticket: false } : prev);
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to resolve ticket");
                            } finally {
                              setResolvingTicketId(null);
                            }
                          }}
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
                          data-testid={`resolve-ticket-btn-${t.id}`}
                        >
                          {resolvingTicketId === t.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                          Mark Resolved
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}


          <Tabs defaultValue="customer" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-4">
              <TabsTrigger value="customer" className="text-xs sm:text-sm"><User className="w-4 h-4 mr-1 hidden sm:inline" />Customer</TabsTrigger>
              <TabsTrigger value="product" className="text-xs sm:text-sm"><Package className="w-4 h-4 mr-1 hidden sm:inline" />Product</TabsTrigger>
              <TabsTrigger value="samples" className="text-xs sm:text-sm" data-testid="tab-samples"><Beaker className="w-4 h-4 mr-1 hidden sm:inline" />Samples{(derivedSamples?.length || 0) > 0 && <span className="ml-1 text-xs text-blue-600">({derivedSamples.length})</span>}</TabsTrigger>
              <TabsTrigger value="orders" className="text-xs sm:text-sm" data-testid="tab-orders"><Package className="w-4 h-4 mr-1 hidden sm:inline" />Orders{(lead?.order_history?.length || 0) > 0 && <span className="ml-1 text-xs text-emerald-600">({lead.order_history.length})</span>}</TabsTrigger>
              <TabsTrigger value="notes" className="text-xs sm:text-sm" data-testid="tab-notes"><FileText className="w-4 h-4 mr-1 hidden sm:inline" />Notes</TabsTrigger>
            </TabsList>

            {/* Customer Details Tab */}
            <TabsContent value="customer">
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Customer Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {renderSelect("customer_type", "Customer Type", options.customer_types, "Select type")}
                    {renderSelect("payment_type", "Payment Type", ["Cash", "Credit"], "Cash / Credit")}
                    <div className="space-y-2">
                      <Label className="text-slate-500 text-sm">Activity Status</Label>
                      <div className={`${getStatusColor(getComputedStatus(lead).value)} px-3 py-2 rounded-lg text-sm font-medium border flex items-center justify-between`}>
                        <span>{getComputedStatus(lead).label}</span>
                        <span className="text-xs opacity-70">(Auto-computed)</span>
                      </div>
                      <p className="text-xs text-slate-400">{getComputedStatus(lead).description}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-500 text-sm">Region</Label>
                      <p className="text-sm font-medium px-3 py-2 rounded-lg bg-slate-50 text-slate-700">
                        {lead.region || (lead.data_source === 'Bangladesh Customers' ? 'Bangladesh' : 'India')}
                        <span className="text-xs text-slate-400 ml-1">(Auto from Data Source)</span>
                      </p>
                    </div>
                    {renderInput("name", "Contact Person Name", "", { required: true })}
                    {renderInput("contact_designation", "Designation", "e.g., Procurement Head")}
                    {renderInput("contact_linkedin", "LinkedIn URL", "https://linkedin.com/in/...")}
                    {renderInput("email", "Email", "", { required: true })}
                    {renderInput("phone", "Phone", lead?.region === 'Bangladesh' ? "+88XXXXXXXXXX" : "", { required: true })}
                    {renderInput("company_name", "Company Name", "", { required: true })}
                    {lead.region !== "Bangladesh" ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-slate-500 text-sm">GST Number <span className="text-red-500">*</span></Label>
                          {isEditing ? (
                            <>
                              <div className="flex gap-2">
                                <Input
                                  value={editData.gst_number || ""}
                                  onChange={(e) => {
                                    setEditData(prev => ({ ...prev, gst_number: e.target.value.toUpperCase() }));
                                    setGstVerified(null);
                                  }}
                                  placeholder="15-character GSTIN"
                                  className="h-9 font-mono flex-1"
                                  data-testid="edit-gst_number-input"
                                  maxLength={15}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant={gstVerified?.active ? "outline" : "default"}
                                  className={`h-9 ${gstVerified?.active ? "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                                  onClick={handleVerifyGST}
                                  disabled={gstVerifying || !(editData.gst_number?.trim())}
                                  data-testid="gst-verify-btn"
                                >
                                  {gstVerifying ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : gstVerified?.active ? (
                                    <><ShieldCheck className="w-4 h-4 mr-1" />Verified</>
                                  ) : (
                                    <><ShieldCheck className="w-4 h-4 mr-1" />Verify</>
                                  )}
                                </Button>
                              </div>
                              {gstVerified && !gstVerified.active && (
                                <p className="text-xs text-red-600 font-medium" data-testid="gst-status-error">GST Status: {gstVerified.status}</p>
                              )}
                              {gstVerified?.active && (
                                <p className="text-xs text-emerald-600 font-medium" data-testid="gst-status-ok">GST Verified — Active ({gstVerified.constitution})</p>
                              )}
                            </>
                          ) : (
                            <p className="text-slate-900 text-sm">{lead.gst_number || "-"}</p>
                          )}
                        </div>
                        {renderInput("gst_registration_date", "GST Registration Date", "YYYY-MM-DD", { required: true })}
                        {renderSelect("gst_turnover", "GST Turnover", [
                          "0-40L", "40L-1.5Cr", "1.5Cr-5Cr", "5Cr-25Cr", "25Cr-100Cr", "100Cr-500Cr", "500Cr+"
                        ], "Select turnover", { required: true })}
                      </>
                    ) : (
                      renderInput("bin_number", "BIN Number", "BIN for Bangladesh (optional)", { required: false })
                    )}
                    {renderSelect("has_website", "Has Website", ["Yes", "No"], "Yes / No", { required: lead?.region !== 'Bangladesh' })}
                    {(editData.has_website || lead?.has_website) === "Yes" && renderInput("website", "Website")}
                    {renderSelect("business_type", "Business Type",
                      lead?.region === 'Bangladesh'
                        ? ["Garment Manufacturer", "Buying House", "Fabric Trader", "Processing Mill"]
                        : ["Private Limited", "Partnership Firm", "LLP", "Proprietorship", "Limited/Public"],
                      "Select type", { required: true })}
                    {lead?.region === 'Bangladesh' && renderSelect("bgmea_member", "BGMEA Member", ["Yes", "No"], "Yes / No")}
                    {/* BGMEA Number — mandatory when BD + Garment Manufacturer.
                        The backend enforces this on PATCH; UI shows the star so
                        admins know it's required in that combination. */}
                    {lead?.region === 'Bangladesh' && (editData.business_type ?? lead?.business_type) === "Garment Manufacturer" &&
                      renderInput("bgmea_number", "BGMEA Number", "e.g., BGMEA-12345", { required: true })}
                    {renderInput("google_reviews", "Google Reviews", "e.g., 125", { type: "number", min: 0, step: 1 })}
                    {renderInput("google_rating", "Google Rating", "e.g., 4.3", { type: "number", min: 0, max: 5, step: 0.1 })}
                    <div className="space-y-2">
                      <Label className="text-slate-500 text-sm">Business Email</Label>
                      <p className={`text-sm font-medium px-3 py-2 rounded-lg ${
                        lead.business_email === 'Yes' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'
                      }`}>
                        {lead.business_email || '-'}
                        <span className="text-xs text-slate-400 ml-1">(Auto-detected)</span>
                      </p>
                    </div>
                    {renderSelect("city", "City", lead?.region === 'Bangladesh' ? BANGLADESH_CITIES : INDIAN_CITIES, "Select city", { required: true })}
                    {renderSelect("state", lead?.region === 'Bangladesh' ? "Division" : "State", lead?.region === 'Bangladesh' ? BANGLADESH_DIVISIONS : INDIAN_STATES, lead?.region === 'Bangladesh' ? "Select division" : "Select state", { required: true })}
                  </div>
                  <div className="pt-2">
                    {renderInput("address", "Address", "", { required: false })}
                  </div>

                  {/* Secondary Contact - Collapsible */}
                  <div className="pt-4 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={() => setShowSecondaryContact(!showSecondaryContact)}
                      className="flex items-center justify-between w-full p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                      data-testid="toggle-secondary-contact-btn"
                    >
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-semibold text-slate-700">Secondary Contact</span>
                        {(lead.contact_2_name || lead.contact_2_phone || lead.contact_2_email) && !showSecondaryContact && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {lead.contact_2_name || lead.contact_2_phone || lead.contact_2_email}
                          </Badge>
                        )}
                      </div>
                      {showSecondaryContact ? (
                        <ChevronUp className="w-4 h-4 text-slate-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      )}
                    </button>
                    {showSecondaryContact && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-slate-50/50 border border-slate-200 rounded-lg" data-testid="secondary-contact-section">
                        {renderInput("contact_2_name", "Name")}
                        {renderInput("contact_2_designation", "Designation", "e.g., Sourcing Manager")}
                        {renderInput("contact_2_phone", "Phone")}
                        {renderInput("contact_2_email", "Email")}
                        {renderInput("contact_2_linkedin", "LinkedIn URL", "https://linkedin.com/in/...")}
                      </div>
                    )}
                  </div>
                  
                  {/* Data Source & Categories Serviced */}
                  {lead.data_source && (
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Data Source Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-slate-500 text-sm">Data Source</Label>
                          <p className="text-slate-900 text-sm font-medium bg-blue-50 px-3 py-2 rounded-lg">
                            {lead.data_source}
                          </p>
                        </div>
                        {lead.sub_data_source && (
                          <div className="space-y-1">
                            <Label className="text-slate-500 text-sm">Platform</Label>
                            <p className="text-slate-900 text-sm font-medium bg-purple-50 px-3 py-2 rounded-lg">
                              {lead.sub_data_source}
                            </p>
                          </div>
                        )}
                        {Number(lead.order_count || 0) > 0 && (
                          <div className="space-y-1" data-testid="order-exists-card">
                            <Label className="text-slate-500 text-sm">Order Exists in System</Label>
                            <p className="text-emerald-800 text-sm font-medium bg-emerald-50 px-3 py-2 rounded-lg flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              {lead.order_count} order{Number(lead.order_count) > 1 ? 's' : ''}
                              {lead.last_order_count_updated_at && (
                                <span className="text-[10px] text-emerald-600 ml-auto">
                                  Updated {new Date(lead.last_order_count_updated_at).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                        {!Number(lead.order_count || 0) && lead.has_query_in_system && (
                          <div className="space-y-1" data-testid="query-exists-card">
                            <Label className="text-slate-500 text-sm">Query Exists in System</Label>
                            <p className="text-purple-800 text-sm font-medium bg-purple-50 px-3 py-2 rounded-lg flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-purple-600" />
                              Marked as repeat query
                              {lead.query_marked_at && (
                                <span className="text-[10px] text-purple-600 ml-auto">
                                  Marked {new Date(lead.query_marked_at).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                        )}
                        {(lead.erp_rfq_number || lead.erp_last_error) && (
                          <div className="space-y-1" data-testid="erp-card">
                            <Label className="text-slate-500 text-sm">Locofast ERP</Label>
                            {lead.erp_rfq_number ? (
                              <p className="text-blue-900 text-sm font-medium bg-blue-50 px-3 py-2 rounded-lg flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-blue-600" />
                                Pushed as <span className="font-mono font-bold">{lead.erp_rfq_number}</span>
                                {lead.erp_last_pushed_at && (
                                  <span className="text-[10px] text-blue-600 ml-auto">
                                    {new Date(lead.erp_last_pushed_at).toLocaleString()}
                                  </span>
                                )}
                              </p>
                            ) : (
                              <p className="text-rose-800 text-xs font-medium bg-rose-50 px-3 py-2 rounded-lg" title={lead.erp_last_error}>
                                <span className="font-semibold">ERP push failed:</span> {lead.erp_last_error}
                              </p>
                            )}
                          </div>
                        )}
                        {lead.data_source === 'Existing customers' && (
                          <>
                            {lead.category_serviced_1 && (
                              <div className="space-y-1">
                                <Label className="text-slate-500 text-sm">Category Serviced 1</Label>
                                <p className="text-slate-900 text-sm font-medium bg-green-50 px-3 py-2 rounded-lg">
                                  {lead.category_serviced_1}
                                </p>
                              </div>
                            )}
                            {lead.category_serviced_2 && (
                              <div className="space-y-1">
                                <Label className="text-slate-500 text-sm">Category Serviced 2</Label>
                                <p className="text-slate-900 text-sm font-medium bg-green-50 px-3 py-2 rounded-lg">
                                  {lead.category_serviced_2}
                                </p>
                              </div>
                            )}
                            {lead.category_serviced_3 && (
                              <div className="space-y-1">
                                <Label className="text-slate-500 text-sm">Category Serviced 3</Label>
                                <p className="text-slate-900 text-sm font-medium bg-green-50 px-3 py-2 rounded-lg">
                                  {lead.category_serviced_3}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Existing Contact Section - Extracted Component */}
                  <ExistingContactSection
                    lead={lead}
                    editData={editData}
                    setEditData={setEditData}
                    isEditing={isEditing}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            {/* Product Details Tab */}
            <TabsContent value="product">
              {/* ===== Requirements List (from lead.requirements array) ===== */}
              <Card className="border-slate-200 mb-4" data-testid="requirements-section">
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Requirements ({(lead.requirements || []).length})</CardTitle>
                    <p className="text-xs text-slate-500 mt-0.5">Product queries for this customer</p>
                  </div>
                  {!isAdmin && (
                    <Button
                      size="sm"
                      onClick={() => setShowNewRequirementDialog(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="create-new-requirement-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />New Requirement
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {(lead.requirements || []).length > 0 ? (
                    <div className="space-y-3">
                      {(lead.requirements || []).map((req, idx) => {
                        const isBangladesh = (lead.region || "") === "Bangladesh";
                        const statusConfig = isBangladesh ? {
                          first_contact_made: { label: "First Contact Made", bg: "bg-blue-100 text-blue-700" },
                          yardage_sent: { label: "Yardage Sent", bg: "bg-amber-100 text-amber-700" },
                          pi_sent: { label: "PI Sent", bg: "bg-violet-100 text-violet-700" },
                          signed_pi_received: { label: "Signed PI Received", bg: "bg-purple-100 text-purple-700" },
                          lc_issued: { label: "LC Issued", bg: "bg-emerald-100 text-emerald-700" },
                          // Legacy fallbacks (read-only — agents can no longer pick these)
                          swatch_sent: { label: "Swatch Sent (legacy)", bg: "bg-slate-100 text-slate-700" },
                          goods_delivered: { label: "Goods Delivered (legacy)", bg: "bg-emerald-200 text-emerald-800" },
                          new: { label: "New", bg: "bg-slate-100 text-slate-700" },
                        } : {
                          new: { label: "New", bg: "bg-blue-100 text-blue-700" },
                          in_pick_pool: { label: "In Pick Pool", bg: "bg-amber-100 text-amber-700" },
                          lfb_created: { label: "LFB Created", bg: "bg-emerald-100 text-emerald-700" },
                          sample_shared: { label: "Sample Shared", bg: "bg-purple-100 text-purple-700" },
                          order_booked: { label: "Order Booked", bg: "bg-indigo-100 text-indigo-700" },
                          delivered_or_closed: { label: "Delivered / Closed", bg: "bg-slate-200 text-slate-700" },
                        };
                        const bdStatusOptions = [
                          { value: "first_contact_made", label: "First Contact Made" },
                          { value: "yardage_sent", label: "Yardage Sent" },
                          { value: "pi_sent", label: "PI Sent" },
                          { value: "signed_pi_received", label: "Signed PI Received" },
                          { value: "lc_issued", label: "LC Issued" },
                        ];
                        const fallbackKey = isBangladesh ? "first_contact_made" : "new";
                        const st = statusConfig[req.requirement_status] || statusConfig[fallbackKey];
                        const isPushing = pushingReqId === req.req_id;
                        // Edit-lock: once any sample under this requirement has
                        // been dispatched or delivered, non-admins can no longer
                        // edit the requirement or toggle sample_shared. Admin
                        // and super_admin bypass. Rejected keeps it editable so
                        // AM can fix + re-request.
                        const isReqLocked = !isAdmin && Array.isArray(lead?.samples) &&
                          lead.samples.some((s) =>
                            s.req_id === req.req_id &&
                            (s.logistics_status === 'dispatched' || s.logistics_status === 'delivered')
                          );
                        const fabricSummary = [req.fabric_type, req.material_1, req.gsm && `${req.gsm} ${req.fabric_type === 'Denim' ? 'oz' : 'GSM'}`].filter(Boolean).join(' / ') || 'No product details';

                        return (
                          <div key={req.req_id} className="rounded-lg border border-slate-200 bg-white hover:border-blue-300 transition-colors" data-testid={`requirement-${req.req_id}`}>
                            <div
                              className={`flex items-center justify-between p-3 ${isReqLocked ? 'cursor-not-allowed opacity-95' : 'cursor-pointer'}`}
                              onClick={() => {
                                if (isReqLocked) {
                                  toast.info("Sample already dispatched — LFB/Sample Request details are locked. Ask an admin to edit.");
                                  return;
                                }
                                setEditingRequirement({...req});
                              }}
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-xs font-semibold text-slate-600 flex-shrink-0">
                                  {idx + 1}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-800 truncate">{fabricSummary}</span>
                                    {req.lfb_id && (
                                      <span className="text-xs font-mono text-blue-600 font-semibold flex-shrink-0" data-testid={`req-lfb-id-${req.req_id}`}>
                                        LFB #{req.lfb_id}
                                      </span>
                                    )}
                                    {!req.lfb_id && req.requirement_status === 'in_pick_pool' && (
                                      <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 flex-shrink-0" data-testid={`req-awaiting-lfb-${req.req_id}`}>
                                        Awaiting LFB
                                      </span>
                                    )}
                                    {isReqLocked && (
                                      <span
                                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-300 flex-shrink-0"
                                        title="Sample already dispatched — details locked"
                                        data-testid={`req-locked-${req.req_id}`}
                                      >
                                        <Lock className="w-3 h-3" /> Locked
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                    {req.quantity && <span>{req.quantity} {req.quantity_unit || ''}</span>}
                                    {req.width_inches && <span>/ {req.width_inches}"</span>}
                                    {req.order_timeline && <span>/ {req.order_timeline}</span>}
                                    {req.payment_type && <span>/ {req.payment_type}{req.credit_days ? ` (${req.credit_days} days)` : ''}</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                {/* Sample Shared — opens dialog asking Paid/Free + AWB */}
                                <button
                                  type="button"
                                  disabled={isReqLocked}
                                  onClick={() => {
                                    if (isReqLocked) {
                                      toast.info("Sample already dispatched — details locked.");
                                      return;
                                    }
                                    // Non-admins must submit an LFB ID before
                                    // they can mark a sample as shared. Admin
                                    // and super_admin bypass. Existing "shared"
                                    // rows can still be viewed / cleared.
                                    const alreadyShared = req.sample_shared === true || req.sample_shared === 'yes' || req.sample_shared === 'Yes';
                                    const hasLfb = !!(req.lfb_id && String(req.lfb_id).trim());
                                    if (!isAdmin && !hasLfb && !alreadyShared) {
                                      toast.error("Submit an LFB ID first — samples can't be marked shared without one.");
                                      return;
                                    }
                                    setSampleDialog({ open: true, reqId: req.req_id, sample_type: req.sample_type || 'free', sample_awb: req.sample_awb || '', current: req.sample_shared });
                                  }}
                                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded border transition-colors ${
                                    isReqLocked
                                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                                      : (req.sample_shared === true || req.sample_shared === 'yes' || req.sample_shared === 'Yes')
                                        ? 'bg-emerald-500 text-white border-emerald-500'
                                        : 'bg-white text-slate-500 border-slate-200 hover:bg-emerald-50 hover:border-emerald-200'
                                  }`}
                                  data-testid={`sample-shared-btn-${req.req_id}`}
                                  title={isReqLocked ? "Locked — sample dispatched" : "Sample shared? (Paid/Free + AWB)"}
                                >
                                  {(req.sample_shared === true || req.sample_shared === 'yes' || req.sample_shared === 'Yes')
                                    ? `${(req.sample_type || 'free').toLowerCase() === 'paid' ? 'Paid' : 'Free'} sample${req.sample_awb ? ` · ${req.sample_awb}` : ''}`
                                    : 'Sample?'}
                                </button>
                                <Badge className={`text-xs ${st.bg}`}>{st.label}</Badge>
                                {!isAdmin && req.requirement_status === 'new' && !isPushing && !isReqLocked && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7 border-blue-300 text-blue-600 hover:bg-blue-50"
                                    onClick={() => { setPushingReqId(req.req_id); setLfbIdInput(""); }}
                                    data-testid={`push-req-${req.req_id}`}
                                  >
                                    Push to Pick Pool
                                  </Button>
                                )}
                                {!isBangladesh && !isAdmin && (req.requirement_status !== 'new') && (
                                  <Select
                                    value={req.requirement_status}
                                    disabled={isReqLocked}
                                    onValueChange={(val) => handleUpdateRequirementStatus(req.req_id, val)}
                                  >
                                    <SelectTrigger className="w-36 h-7 text-xs" data-testid={`status-select-${req.req_id}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="in_pick_pool">In Pick Pool</SelectItem>
                                      <SelectItem value="lfb_created">LFB Created</SelectItem>
                                      <SelectItem value="sample_shared">Sample Shared</SelectItem>
                                      <SelectItem value="order_booked">Order Booked</SelectItem>
                                      <SelectItem value="delivered_or_closed">Delivered / Closed</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                                {isBangladesh && (
                                  <Select
                                    value={bdStatusOptions.some(o => o.value === req.requirement_status) ? req.requirement_status : ""}
                                    disabled={isReqLocked}
                                    onValueChange={(val) => handleBangladeshStatusChange(req.req_id, val)}
                                  >
                                    <SelectTrigger className="w-44 h-7 text-xs" data-testid={`bd-status-select-${req.req_id}`}>
                                      <SelectValue placeholder="Set status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {bdStatusOptions.map(opt => (
                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            </div>

                            {/* Push to Pick Pool / Submit LFB ID form */}
                            {isPushing && (
                              <div className="px-3 pb-3 pt-1 border-t border-slate-100 bg-blue-50 rounded-b-lg" data-testid={`push-form-${req.req_id}`}>
                                {req.requirement_status === 'in_pick_pool' ? (
                                  <p className="text-xs text-slate-600 mb-2">Submit the LFB ID for this requirement to move it to <strong>LFB Created</strong>.</p>
                                ) : (
                                  <p className="text-xs text-slate-600 mb-2">Push this requirement to Pick Pool. LFB ID is optional — you can submit it later.</p>
                                )}
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={lfbIdInput}
                                    onChange={(e) => {
                                      const val = e.target.value.replace(/[^0-9]/g, '');
                                      setLfbIdInput(val);
                                    }}
                                    placeholder={req.requirement_status === 'in_pick_pool' ? "Enter LFB ID (numeric, required)" : "Enter LFB ID (optional)"}
                                    className="h-8 text-sm flex-1 font-mono"
                                    data-testid={`lfb-id-input-${req.req_id}`}
                                    type="text"
                                    inputMode="numeric"
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    className="h-8 bg-blue-600 hover:bg-blue-700 text-xs"
                                    onClick={() => req.requirement_status === 'in_pick_pool'
                                      ? handleSubmitLfbId(req.req_id)
                                      : handlePushRequirementToPickPool(req.req_id)}
                                    data-testid={`confirm-push-${req.req_id}`}
                                  >
                                    {req.requirement_status === 'in_pick_pool' ? 'Submit LFB' : (lfbIdInput.trim() ? 'Push with LFB' : 'Push (no LFB)')}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 text-xs"
                                    onClick={() => { setPushingReqId(null); setLfbIdInput(""); }}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Status progress bar */}
                            {((isBangladesh && req.requirement_status) || (!isBangladesh && req.requirement_status !== 'new')) && (
                              <div className="px-3 pb-2">
                                {(() => {
                                  const steps = isBangladesh
                                    ? ['first_contact_made', 'yardage_sent', 'pi_sent', 'signed_pi_received', 'lc_issued']
                                    : ['in_pick_pool', 'lfb_created', 'sample_shared', 'order_booked', 'delivered_or_closed'];
                                  const currentIdx = steps.indexOf(req.requirement_status);
                                  return (
                                    <div className="flex gap-1">
                                      {steps.map((step, i) => (
                                        <div key={step} className={`h-1 flex-1 rounded-full ${i <= currentIdx ? 'bg-emerald-400' : 'bg-slate-200'}`} />
                                      ))}
                                    </div>
                                  );
                                })()}
                                {isBangladesh && req.requirement_status === 'yardage_sent' && (() => {
                                  const paid = req.yardage_paid;
                                  const mode = req.yardage_mode;
                                  const awb = req.yardage_awb;
                                  if (!paid && !mode) return null;
                                  return (
                                    <div className="mt-2 text-[11px] text-slate-600 flex flex-wrap gap-x-3 gap-y-1" data-testid={`bd-dispatch-info-${req.req_id}`}>
                                      {paid && <span><strong>Type:</strong> {paid === 'paid' ? 'Paid' : 'Free'}</span>}
                                      {mode && <span><strong>Via:</strong> {mode === 'courier' ? 'Courier' : 'By Hand'}</span>}
                                      {mode === 'courier' && awb && <span className="font-mono"><strong>AWB:</strong> {awb}</span>}
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <p className="text-slate-400 text-sm mb-2">No requirements yet</p>
                      {!isAdmin && (
                        <Button size="sm" onClick={() => setShowNewRequirementDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-1" /> Add First Requirement
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ===== Product / Fabric Details (main lead form) ===== */}
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Product / Fabric Details</CardTitle>
                  <p className="text-xs text-slate-500 mt-1"><span className="text-red-500">*</span> Required for Pick Pool</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Basic Fabric Info */}
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Basic Info</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {renderSelect("fabric_type", "Fabric Type", options.fabric_types, "Select type", { pickPoolRequired: true })}
                      {renderSelect("fabric_category", "Fabric Category", ["Woven", "Knits", "Non-woven"], "Select category")}
                      {editData.fabric_type === "Dyed" && 
                        renderSelect("color", "Color", options.colors, "Select color", { pickPoolRequired: true })}
                      {(editData.fabric_type === "Dyed" || editData.fabric_type === "Printed") && 
                        renderSelect("finish", "Finish", options.finishes, "Select finish")}
                      {editData.fabric_type === "Denim" && 
                        renderSelect("wash", "Wash", ["Raw", "One Wash", "Stone Wash", "Acid Wash", "Enzyme Wash", "Bleach Wash"], "Select wash")}
                    </div>
                  </div>

                  {/* Weight & Width */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Weight & Width</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Weight Unit</Label>
                        <p className="text-slate-900 text-sm font-medium bg-slate-50 px-3 py-2 rounded-lg">
                          {editData.fabric_type === "Denim" ? "Oz" : "Grams (GSM)"}
                          <span className="text-xs text-slate-400 ml-2">(Auto)</span>
                        </p>
                      </div>
                      {editData.fabric_type === "Denim" ? (
                        <div className="space-y-2">
                          <Label className="text-slate-500 text-sm">Weight (oz)<span className="text-red-500 ml-1">*</span></Label>
                          {isEditing ? (
                            <Select value={editData.gsm || ""} onValueChange={(v) => setEditData({ ...editData, gsm: v })}>
                              <SelectTrigger className="h-9" data-testid="edit-oz-select">
                                <SelectValue placeholder="Select oz" />
                              </SelectTrigger>
                              <SelectContent>
                                {DENIM_OZ_OPTIONS.map((oz) => (
                                  <SelectItem key={oz} value={oz}>{oz} oz</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="text-slate-900 text-sm">{lead.gsm ? `${lead.gsm} oz` : "-"}</p>
                          )}
                        </div>
                      ) : (
                        renderSelect("gsm", "GSM", options.gsm_options, "Select GSM", { pickPoolRequired: true })
                      )}
                      {renderSelect("width_inches", "Width (inches)", options.width_options?.slice(0, 60), "Select width", { pickPoolRequired: true })}
                    </div>
                  </div>

                  {/* Fabric Construction - EPI/PPI - Hidden for Denim */}
                  {editData.fabric_type !== "Denim" && (
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Fabric Construction</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">EPI (Ends Per Inch / Warp)</Label>
                        {isEditing ? (
                          <Select value={editData.epi || ""} onValueChange={(v) => setEditData({ ...editData, epi: v })}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Select EPI" /></SelectTrigger>
                            <SelectContent className="max-h-[200px]">{EPI_PPI_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.epi || "-"}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">PPI (Picks Per Inch / Weft)</Label>
                        {isEditing ? (
                          <Select value={editData.ppi || ""} onValueChange={(v) => setEditData({ ...editData, ppi: v })}>
                            <SelectTrigger className="h-9"><SelectValue placeholder="Select PPI" /></SelectTrigger>
                            <SelectContent className="max-h-[200px]">{EPI_PPI_OPTIONS.map((v) => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.ppi || "-"}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Composition */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                      Composition
                      {isEditing && (() => {
                        const pct1 = parseInt(editData.material_1_percentage) || 0;
                        const pct2 = parseInt(editData.material_2_percentage) || 0;
                        const pct3 = parseInt(editData.material_3_percentage) || 0;
                        const pct4 = parseInt(editData.material_4_percentage) || 0;
                        const total = pct1 + pct2 + pct3 + pct4;
                        if (total > 0) {
                          return (
                            <span className={`ml-2 text-xs font-normal ${total === 100 ? 'text-green-600' : 'text-red-500'}`}>
                              (Total: {total}% {total === 100 ? '✓' : '- must equal 100%'})
                            </span>
                          );
                        }
                        return null;
                      })()}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Material 1 */}
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Material 1<span className="text-red-500 ml-1">*</span></Label>
                        {isEditing ? (
                          <div className="flex gap-2">
                            <Select value={editData.material_1 || ""} onValueChange={(v) => setEditData({ ...editData, material_1: v })}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={editData.material_1_percentage || ""} onValueChange={(v) => setEditData({ ...editData, material_1_percentage: v })} disabled={!editData.material_1}>
                              <SelectTrigger className="h-9 w-20"><SelectValue placeholder="%" /></SelectTrigger>
                              <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.material_1 ? `${lead.material_1} ${lead.material_1_percentage || ""}%` : "-"}</p>
                        )}
                      </div>
                      {/* Material 2 */}
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Material 2</Label>
                        {isEditing ? (
                          <div className="flex gap-2">
                            <Select value={editData.material_2 || ""} onValueChange={(v) => setEditData({ ...editData, material_2: v })}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={editData.material_2_percentage || ""} onValueChange={(v) => setEditData({ ...editData, material_2_percentage: v })} disabled={!editData.material_2}>
                              <SelectTrigger className="h-9 w-20"><SelectValue placeholder="%" /></SelectTrigger>
                              <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.material_2 ? `${lead.material_2} ${lead.material_2_percentage || ""}%` : "-"}</p>
                        )}
                      </div>
                      {/* Material 3 */}
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Material 3</Label>
                        {isEditing ? (
                          <div className="flex gap-2">
                            <Select value={editData.material_3 || ""} onValueChange={(v) => setEditData({ ...editData, material_3: v })}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={editData.material_3_percentage || ""} onValueChange={(v) => setEditData({ ...editData, material_3_percentage: v })} disabled={!editData.material_3}>
                              <SelectTrigger className="h-9 w-20"><SelectValue placeholder="%" /></SelectTrigger>
                              <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.material_3 ? `${lead.material_3} ${lead.material_3_percentage || ""}%` : "-"}</p>
                        )}
                      </div>
                      {/* Material 4 */}
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Material 4</Label>
                        {isEditing ? (
                          <div className="flex gap-2">
                            <Select value={editData.material_4 || ""} onValueChange={(v) => setEditData({ ...editData, material_4: v })}>
                              <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={editData.material_4_percentage || ""} onValueChange={(v) => setEditData({ ...editData, material_4_percentage: v })} disabled={!editData.material_4}>
                              <SelectTrigger className="h-9 w-20"><SelectValue placeholder="%" /></SelectTrigger>
                              <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.material_4 ? `${lead.material_4} ${lead.material_4_percentage || ""}%` : "-"}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Yarn Count */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                      Yarn Count {(lead.fabric_type === "Denim" || editData.fabric_type === "Denim" || lead.material_1 === "Polyester" || editData.material_1 === "Polyester") && <span className="text-xs font-normal text-slate-500">(in Denier)</span>}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(lead.fabric_type === "Denim" || editData.fabric_type === "Denim" || lead.material_1 === "Polyester" || editData.material_1 === "Polyester") ? (
                        <>
                          <div className="space-y-2">
                            <Label className="text-slate-500 text-sm">Warp Count (Denier)</Label>
                            {isEditing ? (
                              <Select value={editData.warp_count || ""} onValueChange={(v) => setEditData({ ...editData, warp_count: v })}>
                                <SelectTrigger className="h-9"><SelectValue placeholder="Select denier" /></SelectTrigger>
                                <SelectContent>{DENIER_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : (
                              <p className="text-slate-900 text-sm">{lead.warp_count || "-"}</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-500 text-sm">Weft Count (Denier)</Label>
                            {isEditing ? (
                              <Select value={editData.weft_count || ""} onValueChange={(v) => setEditData({ ...editData, weft_count: v })}>
                                <SelectTrigger className="h-9"><SelectValue placeholder="Select denier" /></SelectTrigger>
                                <SelectContent>{DENIER_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : (
                              <p className="text-slate-900 text-sm">{lead.weft_count || "-"}</p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label className="text-slate-500 text-sm">Warp Count</Label>
                            <div className="flex gap-2">
                              {isEditing ? (
                                <>
                                  <Select value={editData.warp_ply || ""} onValueChange={(v) => setEditData({ ...editData, warp_ply: v })}>
                                    <SelectTrigger className="h-9 w-24"><SelectValue placeholder="Ply" /></SelectTrigger>
                                    <SelectContent>{options.ply_options?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <Select value={editData.warp_count || ""} onValueChange={(v) => setEditData({ ...editData, warp_count: v })}>
                                    <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Count" /></SelectTrigger>
                                    <SelectContent className="max-h-[200px]">{options.count_options?.slice(0, 100).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                  </Select>
                                </>
                              ) : (
                                <p className="text-slate-900 text-sm">{lead.warp_ply || lead.warp_count ? `${lead.warp_ply || ""} ${lead.warp_count || ""}` : "-"}</p>
                              )}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-slate-500 text-sm">Weft Count</Label>
                            <div className="flex gap-2">
                              {isEditing ? (
                                <>
                                  <Select value={editData.weft_ply || ""} onValueChange={(v) => setEditData({ ...editData, weft_ply: v })}>
                                    <SelectTrigger className="h-9 w-24"><SelectValue placeholder="Ply" /></SelectTrigger>
                                    <SelectContent>{options.ply_options?.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                  </Select>
                                  <Select value={editData.weft_count || ""} onValueChange={(v) => setEditData({ ...editData, weft_count: v })}>
                                    <SelectTrigger className="h-9 flex-1"><SelectValue placeholder="Count" /></SelectTrigger>
                                    <SelectContent className="max-h-[200px]">{options.count_options?.slice(0, 100).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                  </Select>
                                </>
                              ) : (
                                <p className="text-slate-900 text-sm">{lead.weft_ply || lead.weft_count ? `${lead.weft_ply || ""} ${lead.weft_count || ""}` : "-"}</p>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Shrinkage & Stretch - ONLY for Denim */}
                  {(lead.fabric_type === "Denim" || editData.fabric_type === "Denim") && (
                    <div className="pt-4 border-t border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Properties (Denim Only)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderSelect("weft_shrinkage", "Weft Shrinkage (%)", options.shrinkage_options, "Select %")}
                        {renderSelect("stretch_percent", "Stretch (%)", options.stretch_options, "Select %")}
                      </div>
                    </div>
                  )}

                  {/* Quantity */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Quantity Required</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Quantity<span className="text-red-500 ml-1">*</span></Label>
                        {isEditing ? (
                          <Input
                            value={editData.quantity || ""}
                            onChange={(e) => setEditData({ ...editData, quantity: e.target.value })}
                            placeholder="e.g., 5000"
                            className="h-9"
                            data-testid="edit-quantity-input"
                          />
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.quantity || "-"}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Unit<span className="text-red-500 ml-1">*</span></Label>
                        {(editData.fabric_category === "Woven" || editData.fabric_category === "Knits") ? (
                          <p className="text-slate-900 text-sm font-medium bg-slate-50 px-3 py-2 rounded-lg">
                            {editData.fabric_category === "Woven" ? "Meters" : "Kgs"}
                            <span className="text-xs text-slate-400 ml-2">(Auto)</span>
                          </p>
                        ) : isEditing ? (
                          <Select
                            value={editData.quantity_unit || ""}
                            onValueChange={(value) => setEditData({ ...editData, quantity_unit: value })}
                          >
                            <SelectTrigger className="h-9" data-testid="edit-quantity-unit-select">
                              <SelectValue placeholder="Select unit" />
                            </SelectTrigger>
                            <SelectContent>
                              {(options.quantity_units || ["Yards", "Meters", "Kgs"]).map((unit) => (
                                <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-slate-900 text-sm">{lead.quantity_unit || "-"}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sample Information */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Sample Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-slate-500 text-sm">Sample Shared by Customer?<span className="text-red-500 ml-1">*</span></Label>
                        {isEditing ? (
                          <div className="flex items-center gap-3">
                            <Button
                              type="button"
                              variant={editData.sample_shared ? "default" : "outline"}
                              size="sm"
                              onClick={() => setEditData({ ...editData, sample_shared: true })}
                              className={editData.sample_shared ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                            >
                              Yes
                            </Button>
                            <Button
                              type="button"
                              variant={!editData.sample_shared ? "default" : "outline"}
                              size="sm"
                              onClick={() => setEditData({ ...editData, sample_shared: false })}
                              className={!editData.sample_shared ? "bg-slate-600 hover:bg-slate-700" : ""}
                            >
                              No
                            </Button>
                          </div>
                        ) : (
                          <p className={`text-sm font-medium ${lead.sample_shared ? 'text-emerald-600' : 'text-slate-500'}`}>
                            {lead.sample_shared ? 'Yes' : 'No'}
                          </p>
                        )}
                      </div>
                      
                      {(lead.sample_shared || editData.sample_shared) && (
                        <div className="space-y-3">
                          <Label className="text-slate-500 text-sm">Sample Image</Label>
                          {lead.sample_image_url ? (
                            <div className="space-y-2">
                              <a 
                                href={`${API.replace('/api', '')}${lead.sample_image_url}${lead.sample_image_url.includes('?') ? '&' : '?'}auth=${localStorage.getItem('crm_token') || ''}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-32 h-32 rounded-lg border border-slate-200 overflow-hidden hover:border-blue-500 transition-colors"
                              >
                                <img 
                                  src={`${API.replace('/api', '')}${lead.sample_image_url}${lead.sample_image_url.includes('?') ? '&' : '?'}auth=${localStorage.getItem('crm_token') || ''}`} 
                                  alt="Sample" 
                                  className="w-full h-full object-cover"
                                />
                              </a>
                              <p className="text-xs text-slate-500">Click to view full size</p>
                              <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-md cursor-pointer hover:bg-blue-100 transition-colors">
                                <Upload className="w-3 h-3" />
                                Replace Image
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={handleUploadSample}
                                  className="hidden"
                                  disabled={isUploadingSample}
                                />
                              </label>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-sm text-slate-400 italic">No image uploaded</p>
                              <label className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md cursor-pointer hover:bg-blue-700 transition-colors">
                                {isUploadingSample ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Uploading...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Upload Sample Image
                                  </>
                                )}
                                <input
                                  type="file"
                                  accept="image/jpeg,image/png,image/webp"
                                  onChange={handleUploadSample}
                                  className="hidden"
                                  disabled={isUploadingSample}
                                  data-testid="sample-upload-input"
                                />
                              </label>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Samples Tab */}
            <TabsContent value="samples">
              <Card className="border-slate-200">
                <CardHeader className="pb-4 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Beaker className="w-5 h-5 text-blue-600" />
                    Sample History
                    {(derivedSamples?.length || 0) > 0 && <span className="text-sm font-normal text-slate-500">({derivedSamples.length})</span>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!derivedSamples || derivedSamples.length === 0) ? (
                    <div className="py-10 text-center text-slate-400 text-sm">
                      No samples attached to this lead yet. Use <strong className="text-slate-600">Upload Data → Sample Upload</strong> to push samples here.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-y border-slate-200">
                          <tr>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Sample Date</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Product Name</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">LFB ID</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Logistics</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Source</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Initiated By</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {[...derivedSamples].sort((a, b) => String(b.sample_date || '').localeCompare(String(a.sample_date || ''))).map((s) => (
                            <tr key={s.id} className="hover:bg-slate-50" data-testid={`lead-sample-${s.id}`}>
                              <td className="py-2 px-3 text-slate-700">{formatSampleDate(s.sample_date)}</td>
                              <td className="py-2 px-3 font-medium text-slate-900">{s.product_name || '—'}</td>
                              <td className="py-2 px-3 font-mono text-xs text-blue-600">{s.lfb_id || '—'}</td>
                              <td className="py-2 px-3">
                                {s.logistics_status === 'dispatched' && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">Dispatched</span>
                                )}
                                {s.logistics_status === 'rejected' && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200" title={s.rejection_reason || ''}>Rejected</span>
                                )}
                                {!s.logistics_status && <span className="text-slate-400 text-xs">—</span>}
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-xs px-2 py-0.5 rounded ${s.source === 'bulk_upload' ? 'bg-blue-50 text-blue-700' : s.source === 'requirement' ? 'bg-emerald-50 text-emerald-700' : s.source === 're_request' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                  {s.source === 'bulk_upload' ? 'Bulk Upload' : s.source === 'requirement' ? 'Requirement' : s.source === 're_request' ? 'Re-request' : 'Manual'}
                                </span>
                              </td>
                              <td className="py-2 px-3" data-testid={`sample-initiated-by-${s.id}`}>
                                {s.created_by_name ? (
                                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                    <User className="w-3 h-3" />
                                    {s.created_by_name}
                                  </span>
                                ) : <span className="text-slate-400 text-xs">—</span>}
                              </td>
                              <td className="py-2 px-3">
                                {s.logistics_status === 'rejected' && (isAdmin || (lead?.assigned_to === user?.id)) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-7 text-xs"
                                    onClick={() => handleReRequestSample(s.id)}
                                    data-testid={`re-request-sample-${s.id}`}
                                    title="Create a fresh sample under the same LFB — rejection history stays"
                                  >
                                    Re-request
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-emerald-600" />
                    Order History {(lead?.order_history?.length || 0) > 0 && (
                      <span className="text-sm font-normal text-slate-500">({lead.order_history.length})</span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(!lead?.order_history || lead.order_history.length === 0) ? (
                    <div className="py-8 text-center text-slate-400 text-sm">
                      No orders captured for this lead yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-y border-slate-200">
                          <tr>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Order Date</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Order Lead ID</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Fabric Type</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Composition</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                            <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</th>
                            <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Invoice ₹</th>
                            {isSuperAdmin && (
                              <th className="text-right py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-20">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {[...lead.order_history]
                            .sort((a, b) => (formatSampleDate(b.order_date) || '').localeCompare(formatSampleDate(a.order_date) || ''))
                            .map((o, idx) => (
                              <tr key={`${o.id || idx}-${o.lead_id || ''}`} className="hover:bg-slate-50" data-testid={`lead-order-${idx}`}>
                                <td className="py-2 px-3 text-slate-700">{formatSampleDate(o.order_date)}</td>
                                <td className="py-2 px-3 font-mono text-xs text-emerald-700">{o.lead_id || '—'}</td>
                                <td className="py-2 px-3 text-slate-700">{o.fabric_type || '—'}</td>
                                <td className="py-2 px-3 text-slate-700">{o.composition || '—'}</td>
                                <td className="py-2 px-3 text-right text-slate-700 tabular-nums">{o.quantity != null ? Number(o.quantity).toLocaleString() : '—'}</td>
                                <td className="py-2 px-3">
                                  {o.order_status ? (
                                    <span className={`inline-block text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded ${
                                      o.order_status === 'Delivered' ? 'bg-emerald-100 text-emerald-700'
                                      : o.order_status === 'Cancelled' ? 'bg-rose-100 text-rose-700'
                                      : o.order_status === 'Shipped' || o.order_status === 'Out for delivery' ? 'bg-blue-100 text-blue-700'
                                      : o.order_status === 'Processing' ? 'bg-amber-100 text-amber-700'
                                      : 'bg-slate-100 text-slate-700'
                                    }`}>{o.order_status}</span>
                                  ) : <span className="text-slate-400">—</span>}
                                </td>
                                <td className="py-2 px-3 text-right text-slate-700 tabular-nums">{o.invoice_amount != null ? Number(o.invoice_amount).toLocaleString() : '—'}</td>
                                {isSuperAdmin && (
                                  <td className="py-2 px-3">
                                    <div className="flex items-center justify-end gap-1.5">
                                      <button type="button" onClick={() => openOrderEdit(o)}
                                        className="text-slate-500 hover:text-blue-600"
                                        title="Edit order"
                                        data-testid={`lead-order-edit-${idx}`}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </button>
                                      <button type="button" onClick={() => setOrderDeleting({ order_slot_id: o.id, order_lead_id: o.lead_id })}
                                        className="text-slate-500 hover:text-rose-600"
                                        title="Delete order"
                                        data-testid={`lead-order-delete-${idx}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes">
              <Card className="border-slate-200">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Notes & Call History</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Notes - auto-saved on typing (debounced) */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-500">Notes</Label>
                      <span className="text-xs text-slate-400" data-testid="notes-save-status">
                        {notesSaveStatus}
                      </span>
                    </div>
                    <Textarea
                      value={editData.notes || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditData(prev => ({ ...prev, notes: val }));
                        setNotesSaveStatus("");
                        // Debounced auto-save
                        if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
                        notesTimerRef.current = setTimeout(async () => {
                          try {
                            setNotesSaveStatus("Saving...");
                            await axios.patch(`${API}/leads/${id}`, { notes: val });
                            setLead(prev => ({ ...prev, notes: val }));
                            setNotesSaveStatus("Saved");
                          } catch {
                            setNotesSaveStatus("Failed to save");
                          }
                        }, 1000);
                      }}
                      rows={4}
                      placeholder="Add notes about this lead..."
                      data-testid="edit-notes-input"
                    />
                  </div>

                  {/* Call History */}
                  <div className="pt-4 border-t border-slate-200">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Call History</h4>
                    {calls.length > 0 ? (
                      <div className="space-y-2">
                        {calls.map((call) => {
                          const isBD = (lead?.region || '') === 'Bangladesh';
                          const vstatus = call.verification_status || 'verified';
                          return (
                          <div key={call.id} className="p-3 rounded-md bg-slate-50 space-y-2" data-testid={`call-${call.id}`}>
                            <div className="flex items-center gap-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${call.status === 'completed' ? 'bg-emerald-100' : ['failed','busy','no_answer'].includes(call.status) ? 'bg-red-100' : 'bg-blue-100'}`}>
                                <Phone className={`w-4 h-4 ${call.status === 'completed' ? 'text-emerald-600' : ['failed','busy','no_answer'].includes(call.status) ? 'text-red-600' : 'text-blue-600'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-mono text-sm text-slate-900">{call.phone_number}</p>
                                  <span className={`text-xs font-medium capitalize px-2 py-0.5 rounded ${
                                    call.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 
                                    ['failed','busy','no_answer'].includes(call.status) ? 'bg-red-100 text-red-700' : 
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {call.status?.replace('_', ' ')}
                                  </span>
                                  <RecordingBadge hasRecording={!!call.recording_url} />
                                  {/* Bangladesh Integrity verification flag */}
                                  {isBD && vstatus === 'pending' && (
                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" title="5+ min call missing screenshot/note proof" data-testid={`call-verify-pending-${call.id}`}>
                                      🚩 Screenshot Needed
                                    </span>
                                  )}
                                  {isBD && vstatus === 'zeroed' && (
                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-200 text-slate-600" title="Manager zeroed: excluded from health score" data-testid={`call-verify-zeroed-${call.id}`}>
                                      Zeroed
                                    </span>
                                  )}
                                  {isBD && vstatus === 'verified' && call.verification_reason && (
                                    <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" title={`Verified via ${call.verification_reason}`} data-testid={`call-verified-${call.id}`}>
                                      ✓ {call.verification_reason}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                                  <span className="flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium" data-testid={`call-agent-${call.id}`}>
                                    <User className="w-3 h-3" />
                                    {call.agent_name || call.caller_name || 'Unknown Agent'}
                                  </span>
                                  <span>{new Date(call.created_at).toLocaleString()}</span>
                                  <span className="font-medium text-slate-700">
                                    Duration: {call.duration_seconds > 0 ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s` : '0s'}
                                  </span>
                                  {call.screenshot_url && (
                                    <a
                                      href={
                                        call.screenshot_url.startsWith('http')
                                          ? call.screenshot_url
                                          : `${BACKEND_URL}${call.screenshot_url}${call.screenshot_url.includes('/api/uploads/') && !call.screenshot_url.startsWith('/api/uploads/call_screenshots/') ? `?auth=${localStorage.getItem('crm_token') || ''}` : ''}`
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                      data-testid={`call-screenshot-link-${call.id}`}
                                    >
                                      View screenshot
                                    </a>
                                  )}
                                </div>
                                {/* Manager-only Zero/Re-verify actions for BD pending/verified calls */}
                                {isBD && isAdmin && (vstatus === 'pending' || vstatus === 'verified') && (
                                  <div className="mt-2 flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[11px] px-2 border-red-300 text-red-600 hover:bg-red-50"
                                      onClick={() => handleSetCallVerification(call.id, 'zeroed')}
                                      data-testid={`zero-call-${call.id}`}
                                    >
                                      Zero Score
                                    </Button>
                                  </div>
                                )}
                                {isBD && isAdmin && vstatus === 'zeroed' && (
                                  <div className="mt-2 flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-6 text-[11px] px-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
                                      onClick={() => handleSetCallVerification(call.id, 'verified')}
                                      data-testid={`unzero-call-${call.id}`}
                                    >
                                      Re-verify
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                            <CallRecordingPlayer call={call} compact />
                            {call.notes && (
                              <div className="mt-1 px-2 py-1.5 rounded bg-white border-l-2 border-blue-300 text-xs text-slate-700 whitespace-pre-wrap" data-testid={`call-notes-${call.id}`}>
                                <span className="font-semibold text-slate-500 uppercase text-[10px] block mb-0.5">Notes</span>
                                {call.notes}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center py-6 text-slate-400 text-sm">No calls recorded yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar - Follow-ups & Quick Info */}
        <div className="space-y-6">
          {/* LFB History Timeline */}
          <LfbHistorySection lead={lead} />

          {/* Bangladesh Health Score (PDF: BD Manager Guide) */}
          {lead?.bd_health && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="bd-health-section">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Health Score
                <span className={`ml-auto text-lg font-bold ${
                  lead.bd_health.avg_zone === 'healthy' ? 'text-emerald-600' :
                  lead.bd_health.avg_zone === 'at_risk' ? 'text-amber-600' : 'text-red-600'
                }`} data-testid="bd-health-avg">
                  {lead.bd_health.avg_health}/60
                </span>
              </h4>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${
                  lead.bd_health.avg_zone === 'healthy' ? 'bg-emerald-100 text-emerald-700' :
                  lead.bd_health.avg_zone === 'at_risk' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`} data-testid="bd-health-zone">
                  {lead.bd_health.avg_zone === 'healthy' ? 'Healthy' : lead.bd_health.avg_zone === 'at_risk' ? 'At Risk' : 'Dead'}
                </span>
                <span className="text-[10px] text-slate-500">
                  Avg across {(lead.bd_health.requirements || []).length} requirement{(lead.bd_health.requirements || []).length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs" title="Number of distinct days called in last 30d">
                  <span className="w-28 text-slate-500">Call Frequency</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className={`h-2 rounded-full ${lead.bd_health.call_freq_score >= 6 ? 'bg-emerald-400' : lead.bd_health.call_freq_score >= 3 ? 'bg-amber-400' : 'bg-red-300'}`}
                         style={{ width: `${(lead.bd_health.call_freq_score / 10) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right font-medium text-slate-700">{lead.bd_health.call_freq_score}/10</span>
                </div>
                <div className="flex items-center gap-2 text-xs" title="Average duration of last 5 calls in last 30d">
                  <span className="w-28 text-slate-500">Call Duration</span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className={`h-2 rounded-full ${lead.bd_health.call_duration_score >= 7 ? 'bg-emerald-400' : lead.bd_health.call_duration_score >= 3 ? 'bg-amber-400' : 'bg-red-300'}`}
                         style={{ width: `${(lead.bd_health.call_duration_score / 10) * 100}%` }} />
                  </div>
                  <span className="w-10 text-right font-medium text-slate-700">{lead.bd_health.call_duration_score}/10</span>
                </div>
              </div>
              {(lead.bd_health.requirements || []).length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
                  <p className="text-[10px] uppercase font-semibold text-slate-400 tracking-wider mb-1">Per Requirement</p>
                  {lead.bd_health.requirements.map((r, idx) => (
                    <div key={r.req_id || idx} className="flex items-center justify-between text-xs" data-testid={`bd-req-health-${r.req_id}`}>
                      <span className="text-slate-600 truncate">
                        #{idx + 1} {(r.requirement_status || 'unset').replace(/_/g, ' ')}
                      </span>
                      <span className={`font-bold ${
                        r.zone === 'healthy' ? 'text-emerald-600' :
                        r.zone === 'at_risk' ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {r.health}/60
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-3 italic">
                Auto-computed: Sales Progress (×4) + Call Frequency + Call Duration. Frequency &amp; Duration are from call logs of the last 30 days.
              </p>
              {(lead.bd_red_flags || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-red-100">
                  <p className="text-[10px] uppercase font-semibold text-red-600 tracking-wider mb-2 flex items-center gap-1">
                    🚩 Red Flag Signals
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {lead.bd_red_flags.map(rf => (
                      <span
                        key={rf.code}
                        className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                          rf.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}
                        data-testid={`lead-red-flag-${rf.code}`}
                      >
                        {rf.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ICP Score Breakdown */}
          {scoreBreakdown && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="icp-score-section">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                ICP Score
                {scoreBreakdown.model === "meta" && !scoreBreakdown.not_applicable && (
                  <span
                    className="text-[10px] uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold"
                    data-testid="icp-meta-tag"
                    title="Meta-specific model: Turnover, Registration Type, Brand, GST Age, Calls"
                  >
                    Meta model
                  </span>
                )}
                {scoreBreakdown.not_applicable || scoreBreakdown.total == null ? (
                  <span className="ml-auto text-lg font-bold text-slate-400" data-testid="icp-score-na">NA</span>
                ) : (
                  <span className={`ml-auto text-lg font-bold ${
                    scoreBreakdown.total >= 7 ? 'text-emerald-600' :
                    scoreBreakdown.total >= 4 ? 'text-amber-600' : 'text-red-600'
                  }`}>
                    {scoreBreakdown.total}/10
                  </span>
                )}
              </h4>
              {scoreBreakdown.not_applicable ? (
                <p className="text-xs text-slate-500" data-testid="icp-score-na-message">
                  {scoreBreakdown.message || 'ICP scoring is not applicable for this lead.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(scoreBreakdown.factors || {}).map(([key, f]) => (
                    <div key={key} className="flex items-center gap-2 text-xs">
                      <span className="w-24 text-slate-500 truncate capitalize">{key.replace('_', ' ')}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${f.score >= 70 ? 'bg-emerald-400' : f.score >= 40 ? 'bg-amber-400' : 'bg-red-300'}`}
                          style={{ width: `${f.score}%` }}
                        />
                      </div>
                      <span className="w-8 text-right font-medium text-slate-700">{f.score}</span>
                      <span className="w-20 text-slate-400 truncate text-right">{f.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Email History */}
          {emailHistory.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="email-history-section">
              <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                Email History ({emailHistory.length})
              </h4>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {emailHistory.map((email) => (
                  <div
                    key={email.id}
                    className={`p-3 rounded-lg border ${email.status === 'sent' ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}
                    data-testid={`email-log-${email.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600">To: {email.to_email}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${email.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                        {email.status}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-slate-900 truncate">{email.subject}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      By {email.sent_by} — {new Date(email.created_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Follow-ups Section - Extracted Component */}
          <FollowupsSection
            followups={followups}
            isAdmin={isAdmin}
            showFollowupDialog={showFollowupDialog}
            setShowFollowupDialog={setShowFollowupDialog}
            newFollowup={newFollowup}
            setNewFollowup={setNewFollowup}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            selectedTime={selectedTime}
            setSelectedTime={setSelectedTime}
            handleCreateFollowup={handleCreateFollowup}
            handleCompleteFollowup={handleCompleteFollowup}
            getPriorityColor={getPriorityColor}
          />

          {/* Quick Info Section - Extracted Component */}
          <QuickInfoSection
            lead={lead}
            calls={calls}
            isAdmin={isAdmin}
            pickPoolStatus={pickPoolStatus}
            isPushingToPickPool={isPushingToPickPool}
            showPickPoolError={showPickPoolError}
            setShowPickPoolError={setShowPickPoolError}
            handlePushToPickPool={handlePushToPickPool}
            setIsEditing={setIsEditing}
            formatFieldName={formatFieldName}
            isPushingToERP={isPushingToERP}
            handlePushToERP={handlePushToERP}
          />

          {/* Data Quality Section - Extracted Component */}
          <DataQualitySection
            lead={lead}
            editData={editData}
            setEditData={setEditData}
            isEditing={isEditing}
          />
        </div>
      </div>

      {/* Due Follow-up Dialog - Extracted Component */}
      <DueFollowupDialog
        showDueFollowupPopup={showDueFollowupPopup}
        setShowDueFollowupPopup={setShowDueFollowupPopup}
        dueFollowup={dueFollowup}
        rescheduleDate={rescheduleDate}
        setRescheduleDate={setRescheduleDate}
        rescheduleTime={rescheduleTime}
        setRescheduleTime={setRescheduleTime}
        handleRescheduleFollowup={handleRescheduleFollowup}
        handleCompleteFollowup={handleCompleteFollowup}
        handleClickToCall={handleClickToCall}
        lead={lead}
      />

      {/* Call Choice Dialog — Indian leads */}
      <Dialog open={showCallChoiceDialog} onOpenChange={setShowCallChoiceDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>How do you want to call?</DialogTitle>
            <DialogDescription>{lead?.company_name} — {lead?.phone}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-4">
            <Button
              className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white justify-start gap-3"
              onClick={() => {
                setShowCallChoiceDialog(false);
                handleClickToCall();
              }}
              data-testid="choice-call-panel"
            >
              <Phone className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">Call via Panel</div>
                <div className="text-xs opacity-80">Exotel click-to-call</div>
              </div>
            </Button>
            <Button
              variant="outline"
              className="w-full h-12 justify-start gap-3 border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={() => {
                setShowCallChoiceDialog(false);
                setShowManualCallDialog(true);
              }}
              data-testid="choice-log-manual"
            >
              <Edit2 className="w-5 h-5" />
              <div className="text-left">
                <div className="font-medium">Log Call Manually</div>
                <div className="text-xs opacity-80">Record date, duration & screenshot</div>
              </div>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Call Log Dialog */}
      <Dialog open={showManualCallDialog} onOpenChange={setShowManualCallDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log Call - {lead?.company_name}</DialogTitle>
            <DialogDescription>Manually log a call for this lead</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Call Date *</Label>
                <Input
                  type="date"
                  value={manualCallForm.call_date}
                  readOnly
                  disabled
                  className="bg-slate-100 text-slate-700 cursor-not-allowed"
                  data-testid="detail-manual-call-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Call Time * ({manualCallForm.tz_label || 'IST'})</Label>
                <Input
                  type="time"
                  value={manualCallForm.call_time}
                  readOnly
                  disabled
                  className="bg-slate-100 text-slate-700 cursor-not-allowed"
                  data-testid="detail-manual-call-time"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500 -mt-2" data-testid="detail-call-tz-hint">
              🔒 Locked to current time in {manualCallForm.tz_label === 'BDT' ? 'Bangladesh (BDT)' : 'India (IST)'} — the moment you submit is what gets logged.
            </p>
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select
                value={manualCallForm.status}
                onValueChange={(value) => setManualCallForm(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="detail-manual-call-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="connected">Call Connected</SelectItem>
                  <SelectItem value="requirement_shared">Requirement Shared</SelectItem>
                  <SelectItem value="call_again_later">Call Again Later</SelectItem>
                  <SelectItem value="no_requirement">No Requirement Currently</SelectItem>
                  <SelectItem value="not_connected">Call Not Connected</SelectItem>
                  <SelectItem value="not_relevant">Not Relevant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  min="0"
                  value={manualCallForm.duration_minutes}
                  onChange={(e) => setManualCallForm(prev => ({ ...prev, duration_minutes: e.target.value }))}
                  placeholder="0"
                  data-testid="detail-manual-call-duration-min"
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (seconds)</Label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={manualCallForm.duration_seconds_input}
                  onChange={(e) => setManualCallForm(prev => ({ ...prev, duration_seconds_input: e.target.value }))}
                  placeholder="0"
                  data-testid="detail-manual-call-duration-sec"
                />
              </div>
            </div>
            {/* Call Screenshot — Bangladesh Integrity Rule */}
            {(() => {
              const totalSec = (parseInt(manualCallForm.duration_minutes) || 0) * 60 + (parseInt(manualCallForm.duration_seconds_input) || 0);
              const isBD = (lead?.region || '') === 'Bangladesh';
              const needsProof = isBD && totalSec >= 300; // 5 min
              return (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Call Screenshot
                    {needsProof && (
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700" data-testid="bd-screenshot-flag">
                        Required for 5+ min
                      </span>
                    )}
                  </Label>
                  <Input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => setCallScreenshot(e.target.files[0] || null)}
                    data-testid="detail-manual-call-screenshot"
                    className={`text-sm ${needsProof && !callScreenshot ? 'border-red-300' : ''}`}
                  />
                  {callScreenshot && (
                    <p className="text-xs text-emerald-600">{callScreenshot.name}</p>
                  )}
                  {needsProof && !callScreenshot && (
                    <p className="text-[11px] text-red-600">
                      🚩 Bangladesh Integrity Rule: 5+ min calls need a WhatsApp screenshot OR a note of 20+ characters as proof. Without either, the call will be flagged and a manager can zero its score.
                    </p>
                  )}
                </div>
              );
            })()}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={manualCallForm.notes}
                onChange={(e) => setManualCallForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Call notes..."
                data-testid="detail-manual-call-notes"
              />
            </div>

            {/* Mandatory Follow-up — required for agents on every call */}
            <div className="space-y-3 pt-3 mt-2 border-t border-slate-200" data-testid="manual-call-followup-section">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-amber-600" />
                  Schedule Follow-up
                  <span className="text-[10px] uppercase font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Optional</span>
                </Label>
                <span className="text-[11px] text-slate-400">auto: call date + 7 days</span>
              </div>
              <Textarea
                rows={2}
                value={manualCallForm.followup_description}
                onChange={(e) => setManualCallForm(prev => ({ ...prev, followup_description: e.target.value }))}
                placeholder="What is the next action? e.g. Call back to share fabric swatch, send pricing, follow on payment…"
                data-testid="detail-manual-call-followup-desc"
              />
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1 col-span-1">
                  <Label className="text-xs text-slate-500">Follow-up Date</Label>
                  <Input
                    type="date"
                    value={manualCallForm.followup_date}
                    onChange={(e) => setManualCallForm(prev => ({ ...prev, followup_date: e.target.value, followup_date_touched: true }))}
                    data-testid="detail-manual-call-followup-date"
                  />
                </div>
                <div className="space-y-1 col-span-1">
                  <Label className="text-xs text-slate-500">Time</Label>
                  <Input
                    type="time"
                    value={manualCallForm.followup_time}
                    onChange={(e) => setManualCallForm(prev => ({ ...prev, followup_time: e.target.value }))}
                    data-testid="detail-manual-call-followup-time"
                  />
                </div>
                <div className="space-y-1 col-span-1">
                  <Label className="text-xs text-slate-500">Priority</Label>
                  <Select
                    value={manualCallForm.followup_priority}
                    onValueChange={(v) => setManualCallForm(prev => ({ ...prev, followup_priority: v }))}
                  >
                    <SelectTrigger data-testid="detail-manual-call-followup-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualCallDialog(false)}>Cancel</Button>
            <Button onClick={handleManualCallSubmit} disabled={isUploadingScreenshot || isSubmittingManualCall} data-testid="detail-manual-call-submit">
              {(isUploadingScreenshot || isSubmittingManualCall) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
              {isSubmittingManualCall ? 'Logging…' : 'Log Call'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-[550px]" data-testid="email-compose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Send Email — {lead?.company_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>To</Label>
              <Input
                value={emailForm.to}
                onChange={(e) => setEmailForm(prev => ({ ...prev, to: e.target.value }))}
                placeholder="recipient@example.com"
                data-testid="email-to-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject..."
                data-testid="email-subject-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Body</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                placeholder="Write your email..."
                rows={8}
                data-testid="email-body-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailForm.subject.trim() || !emailForm.body.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="email-send-btn"
            >
              {isSendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              {isSendingEmail ? "Sending..." : "Send Email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Requirement Dialog */}
      <Dialog open={showNewRequirementDialog} onOpenChange={setShowNewRequirementDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[85vh] p-0 flex flex-col" data-testid="new-requirement-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              New Requirement — {lead?.company_name}
            </DialogTitle>
            <DialogDescription>Customer info will be copied automatically. Fill in product details.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-2">
          <div className="space-y-5 py-2">
            {/* Basic Info */}
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Basic Info</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Fabric Type *</Label>
                  <Select value={newRequirement.fabric_type || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, fabric_type: v }))}>
                    <SelectTrigger data-testid="new-req-fabric-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{options?.fabric_types?.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fabric Category</Label>
                  <Select value={newRequirement.fabric_category || ""} onValueChange={(v) => {
                    const updates = { fabric_category: v };
                    if (v === 'Woven') updates.quantity_unit = 'Meters';
                    else if (v === 'Knits') updates.quantity_unit = 'Kgs';
                    setNewRequirement(prev => ({ ...prev, ...updates }));
                  }}>
                    <SelectTrigger data-testid="new-req-fabric-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Woven">Woven</SelectItem>
                      <SelectItem value="Knits">Knits</SelectItem>
                      <SelectItem value="Non-woven">Non-woven</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newRequirement.fabric_type === "Dyed" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Color *</Label>
                    <Select value={newRequirement.color || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, color: v }))}>
                      <SelectTrigger data-testid="new-req-color-required"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{options?.colors?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {newRequirement.fabric_type && newRequirement.fabric_type !== "Dyed" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Color / Shade</Label>
                    <Select value={newRequirement.color || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, color: v }))}>
                      <SelectTrigger data-testid="new-req-color"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{options?.colors?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {(newRequirement.fabric_type === "Dyed" || newRequirement.fabric_type === "Printed") && (
                  <div className="space-y-1">
                    <Label className="text-xs">Finish</Label>
                    <Select value={newRequirement.finish || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, finish: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{options?.finishes?.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {newRequirement.fabric_type === "Denim" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Wash</Label>
                    <Select value={newRequirement.wash || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, wash: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {["Raw", "One Wash", "Stone Wash", "Acid Wash", "Enzyme Wash", "Bleach Wash"].map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Weight & Width */}
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Weight & Width</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{newRequirement.fabric_type === "Denim" ? "Weight (oz) *" : "GSM *"}</Label>
                  {newRequirement.fabric_type === "Denim" ? (
                    <Select value={newRequirement.gsm || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, gsm: v }))}>
                      <SelectTrigger data-testid="new-req-gsm"><SelectValue placeholder="Select oz" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{DENIM_OZ_OPTIONS.map(oz => <SelectItem key={oz} value={oz}>{oz} oz</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={newRequirement.gsm || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, gsm: v }))}>
                      <SelectTrigger data-testid="new-req-gsm"><SelectValue placeholder="Select GSM" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{options?.gsm_options?.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Width (inches) *</Label>
                  <Select value={newRequirement.width_inches || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, width_inches: v }))}>
                    <SelectTrigger data-testid="new-req-width"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">{options?.width_options?.slice(0, 60).map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Fabric Construction - EPI/PPI (hidden for Denim) */}
            {newRequirement.fabric_type !== "Denim" && (
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Fabric Construction</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">EPI (Ends Per Inch)</Label>
                    <Select value={newRequirement.epi || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, epi: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select EPI" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{EPI_PPI_OPTIONS.map(v => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">PPI (Picks Per Inch)</Label>
                    <Select value={newRequirement.ppi || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, ppi: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select PPI" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{EPI_PPI_OPTIONS.map(v => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Composition */}
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Composition</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Material 1 *</Label>
                  <div className="flex gap-2">
                    <Select value={newRequirement.material_1 || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_1: v }))}>
                      <SelectTrigger className="flex-1" data-testid="new-req-material-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newRequirement.material_1_percentage || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_1_percentage: v }))}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Material 2</Label>
                  <div className="flex gap-2">
                    <Select value={newRequirement.material_2 || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_2: v }))}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newRequirement.material_2_percentage || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_2_percentage: v }))}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Material 3</Label>
                  <div className="flex gap-2">
                    <Select value={newRequirement.material_3 || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_3: v }))}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newRequirement.material_3_percentage || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_3_percentage: v }))}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Material 4</Label>
                  <div className="flex gap-2">
                    <Select value={newRequirement.material_4 || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_4: v }))}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newRequirement.material_4_percentage || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, material_4_percentage: v }))}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Yarn Count */}
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Yarn Count {(newRequirement.fabric_type === "Denim" || newRequirement.material_1 === "Polyester") && <span className="text-xs font-normal text-slate-400">(in Denier)</span>}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {(newRequirement.fabric_type === "Denim" || newRequirement.material_1 === "Polyester") ? (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Warp Count (Denier)</Label>
                      <Select value={newRequirement.warp_count || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, warp_count: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select denier" /></SelectTrigger>
                        <SelectContent>{DENIER_OPTIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Weft Count (Denier)</Label>
                      <Select value={newRequirement.weft_count || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, weft_count: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select denier" /></SelectTrigger>
                        <SelectContent>{DENIER_OPTIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-1">
                      <Label className="text-xs">Warp Count</Label>
                      <div className="flex gap-2">
                        <Select value={newRequirement.warp_ply || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, warp_ply: v }))}>
                          <SelectTrigger className="w-24"><SelectValue placeholder="Ply" /></SelectTrigger>
                          <SelectContent>{options?.ply_options?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={newRequirement.warp_count || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, warp_count: v }))}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Count" /></SelectTrigger>
                          <SelectContent className="max-h-[200px]">{options?.count_options?.slice(0, 100).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Weft Count</Label>
                      <div className="flex gap-2">
                        <Select value={newRequirement.weft_ply || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, weft_ply: v }))}>
                          <SelectTrigger className="w-24"><SelectValue placeholder="Ply" /></SelectTrigger>
                          <SelectContent>{options?.ply_options?.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={newRequirement.weft_count || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, weft_count: v }))}>
                          <SelectTrigger className="flex-1"><SelectValue placeholder="Count" /></SelectTrigger>
                          <SelectContent className="max-h-[200px]">{options?.count_options?.slice(0, 100).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Shrinkage & Stretch - Denim only */}
            {newRequirement.fabric_type === "Denim" && (
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Properties (Denim)</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Weft Shrinkage (%)</Label>
                    <Select value={newRequirement.weft_shrinkage || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, weft_shrinkage: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select %" /></SelectTrigger>
                      <SelectContent>{options?.shrinkage_options?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Stretch (%)</Label>
                    <Select value={newRequirement.stretch_percent || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, stretch_percent: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select %" /></SelectTrigger>
                      <SelectContent>{options?.stretch_options?.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Quantity & Timeline */}
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Quantity & Timeline</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Quantity *</Label>
                  <Input
                    value={newRequirement.quantity || ""}
                    onChange={(e) => setNewRequirement(prev => ({ ...prev, quantity: e.target.value }))}
                    placeholder="e.g., 5000"
                    data-testid="new-req-quantity"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Unit</Label>
                  {(newRequirement.fabric_category === "Woven" || newRequirement.fabric_category === "Knits") ? (
                    <p className="text-sm font-medium bg-slate-50 px-3 py-2 rounded-md border">
                      {newRequirement.fabric_category === "Woven" ? "Meters" : "Kgs"}
                      <span className="text-xs text-slate-400 ml-1">(Auto)</span>
                    </p>
                  ) : (
                    <Select value={newRequirement.quantity_unit || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, quantity_unit: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{(options?.quantity_units || ["Yards", "Meters", "Kgs"]).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Order Timeline</Label>
                  <Select value={newRequirement.order_timeline || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, order_timeline: v }))}>
                    <SelectTrigger data-testid="new-req-timeline"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{ORDER_TIMELINE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Payment Terms */}
            <div>
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Payment Terms</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Payment Type</Label>
                  <Select value={newRequirement.payment_type || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, payment_type: v, credit_days: v === 'Cash' ? '' : prev.credit_days }))}>
                    <SelectTrigger data-testid="new-req-payment-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Credit">Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newRequirement.payment_type === "Credit" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Credit Days *</Label>
                    <Select value={newRequirement.credit_days || ""} onValueChange={(v) => setNewRequirement(prev => ({ ...prev, credit_days: v }))}>
                      <SelectTrigger data-testid="new-req-credit-days"><SelectValue placeholder="Select days" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 Days</SelectItem>
                        <SelectItem value="45">45 Days</SelectItem>
                        <SelectItem value="60">60 Days</SelectItem>
                        <SelectItem value="90">90 Days</SelectItem>
                        <SelectItem value="120">120 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
            <Button variant="outline" onClick={() => setShowNewRequirementDialog(false)}>Cancel</Button>
            <Button
              onClick={handleCreateRequirement}
              disabled={isCreatingRequirement}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="submit-new-requirement-btn"
            >
              {isCreatingRequirement ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Requirement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Edit Requirement Dialog ===== */}
      <Dialog open={!!editingRequirement} onOpenChange={(open) => !open && setEditingRequirement(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] p-0 flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-4 h-4" />
              Edit Requirement
              {editingRequirement?.lfb_id && <span className="text-sm font-mono text-blue-600">#{editingRequirement.lfb_id}</span>}
            </DialogTitle>
            <DialogDescription>Update the requirement details for this lead.</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-2">
          {editingRequirement && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Fabric Category *</Label>
                  <Select value={editingRequirement.fabric_category || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, fabric_category: v }))}>
                    <SelectTrigger data-testid="edit-req-fabric-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{options?.fabric_categories?.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fabric Type</Label>
                  <Select value={editingRequirement.fabric_type || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, fabric_type: v }))}>
                    <SelectTrigger data-testid="edit-req-fabric-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">{(editingRequirement.fabric_category === "Denim" ? options?.denim_fabric_types : options?.fabric_types)?.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{editingRequirement?.fabric_type === "Denim" ? "Weight (oz)" : "GSM"}</Label>
                  {editingRequirement?.fabric_type === "Denim" ? (
                    <Select value={editingRequirement.gsm || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, gsm: v }))}>
                      <SelectTrigger><SelectValue placeholder="Select oz" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{DENIM_OZ_OPTIONS.map(oz => <SelectItem key={oz} value={oz}>{oz} oz</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Select value={editingRequirement.gsm || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, gsm: v }))}>
                      <SelectTrigger><SelectValue placeholder="GSM" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{options?.gsm_options?.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Width (inches)</Label>
                  <Select value={editingRequirement.width_inches || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, width_inches: v }))}>
                    <SelectTrigger><SelectValue placeholder="Width" /></SelectTrigger>
                    <SelectContent className="max-h-[200px]">{options?.width_options?.map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Color</Label>
                  <Input value={editingRequirement.color || ""} onChange={(e) => setEditingRequirement(prev => ({ ...prev, color: e.target.value }))} placeholder="Color" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Material 1</Label>
                  <div className="flex gap-2">
                    <Select value={editingRequirement.material_1 || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_1: v }))}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={editingRequirement.material_1_percentage || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_1_percentage: v }))}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Material 2</Label>
                  <div className="flex gap-2">
                    <Select value={editingRequirement.material_2 || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_2: v }))}>
                      <SelectTrigger className="flex-1"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={editingRequirement.material_2_percentage || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_2_percentage: v }))}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Material 3</Label>
                  <div className="flex gap-2">
                    <Select value={editingRequirement.material_3 || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_3: v }))}>
                      <SelectTrigger className="flex-1" data-testid="edit-req-material-3"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={editingRequirement.material_3_percentage || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_3_percentage: v }))} disabled={!editingRequirement.material_3}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Material 4</Label>
                  <div className="flex gap-2">
                    <Select value={editingRequirement.material_4 || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_4: v }))}>
                      <SelectTrigger className="flex-1" data-testid="edit-req-material-4"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COMPOSITION_MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={editingRequirement.material_4_percentage || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, material_4_percentage: v }))} disabled={!editingRequirement.material_4}>
                      <SelectTrigger className="w-20"><SelectValue placeholder="%" /></SelectTrigger>
                      <SelectContent className="max-h-[200px]">{PERCENTAGE_OPTIONS.map(p => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Quantity</Label>
                  <Input value={editingRequirement.quantity || ""} onChange={(e) => setEditingRequirement(prev => ({ ...prev, quantity: e.target.value }))} placeholder="Qty" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Unit</Label>
                  <Select value={editingRequirement.quantity_unit || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, quantity_unit: v }))}>
                    <SelectTrigger><SelectValue placeholder="Unit" /></SelectTrigger>
                    <SelectContent>{(options?.quantity_units || ["Yards", "Meters", "Kgs"]).map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Order Timeline</Label>
                  <Select value={editingRequirement.order_timeline || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, order_timeline: v }))}>
                    <SelectTrigger><SelectValue placeholder="Timeline" /></SelectTrigger>
                    <SelectContent>{ORDER_TIMELINE_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {/* Payment Terms */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Payment Type</Label>
                  <Select value={editingRequirement.payment_type || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, payment_type: v, credit_days: v === 'Cash' ? '' : prev.credit_days }))}>
                    <SelectTrigger data-testid="edit-req-payment-type"><SelectValue placeholder="Cash / Credit" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Credit">Credit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editingRequirement.payment_type === "Credit" && (
                  <div className="space-y-1">
                    <Label className="text-xs">Credit Days</Label>
                    <Select value={editingRequirement.credit_days || ""} onValueChange={(v) => setEditingRequirement(prev => ({ ...prev, credit_days: v }))}>
                      <SelectTrigger data-testid="edit-req-credit-days"><SelectValue placeholder="Select days" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">30 Days</SelectItem>
                        <SelectItem value="45">45 Days</SelectItem>
                        <SelectItem value="60">60 Days</SelectItem>
                        <SelectItem value="90">90 Days</SelectItem>
                        <SelectItem value="120">120 Days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Finish</Label>
                <Input value={editingRequirement.finish || ""} onChange={(e) => setEditingRequirement(prev => ({ ...prev, finish: e.target.value }))} placeholder="Finish details" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Wash</Label>
                <Input value={editingRequirement.wash || ""} onChange={(e) => setEditingRequirement(prev => ({ ...prev, wash: e.target.value }))} placeholder="Wash details" />
              </div>
            </div>
          )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
            <Button variant="outline" onClick={() => setEditingRequirement(null)}>Cancel</Button>
            <Button
              onClick={handleSaveRequirement}
              disabled={isSavingRequirement}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="save-requirement-btn"
            >
              {isSavingRequirement ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Sample Shared Dialog — asks for Paid/Free + AWB whenever sample is marked shared */}
      <Dialog open={sampleDialog.open} onOpenChange={(o) => setSampleDialog((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-md" data-testid="sample-shared-dialog">
          <DialogHeader>
            <DialogTitle>Sample Shared Details</DialogTitle>
            <DialogDescription>Capture sample type and AWB number — used for reporting and target tracking.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div>
              <Label className="text-xs mb-2 block">Sample Type</Label>
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="sample-type-toggle">
                <button
                  type="button"
                  className={`px-4 py-1.5 text-sm font-medium ${sampleDialog.sample_type === 'free' ? 'bg-blue-500 text-white' : 'bg-white text-slate-600 hover:bg-blue-50'}`}
                  onClick={() => setSampleDialog((s) => ({ ...s, sample_type: 'free' }))}
                  data-testid="sample-type-free"
                >Free</button>
                <button
                  type="button"
                  className={`px-4 py-1.5 text-sm font-medium border-l border-slate-200 ${sampleDialog.sample_type === 'paid' ? 'bg-emerald-500 text-white' : 'bg-white text-slate-600 hover:bg-emerald-50'}`}
                  onClick={() => setSampleDialog((s) => ({ ...s, sample_type: 'paid' }))}
                  data-testid="sample-type-paid"
                >Paid</button>
              </div>
            </div>
            <div>
              <Label htmlFor="sample-awb-input" className="text-xs">AWB Number <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input
                id="sample-awb-input"
                value={sampleDialog.sample_awb}
                onChange={(e) => setSampleDialog((s) => ({ ...s, sample_awb: e.target.value }))}
                placeholder="e.g., 1234567890"
                className="mt-1"
                data-testid="sample-awb-input"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-between gap-2 sm:justify-between">
            {/* Mark not shared (clears) */}
            <Button
              variant="outline"
              className="text-rose-600 hover:bg-rose-50"
              onClick={async () => {
                try {
                  await axios.patch(`${API}/leads/${id}/requirements/${sampleDialog.reqId}`, { sample_shared: false, sample_type: '', sample_awb: '' });
                  toast.success("Sample marked as not shared");
                  setSampleDialog({ open: false, reqId: null, sample_type: 'free', sample_awb: '', current: null });
                  fetchLeadData();
                } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
              }}
              data-testid="sample-mark-not-shared"
            >Mark Not Shared</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSampleDialog((s) => ({ ...s, open: false }))}>Cancel</Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={async () => {
                  try {
                    await axios.patch(`${API}/leads/${id}/requirements/${sampleDialog.reqId}`, {
                      sample_shared: true,
                      sample_type: sampleDialog.sample_type,
                      sample_awb: sampleDialog.sample_awb,
                    });
                    toast.success(`${sampleDialog.sample_type === 'paid' ? 'Paid' : 'Free'} sample saved`);
                    setSampleDialog({ open: false, reqId: null, sample_type: 'free', sample_awb: '', current: null });
                    fetchLeadData();
                  } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
                }}
                data-testid="sample-save-shared"
              >Save</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bangladesh Swatch/Yardage Dispatch Dialog */}
      <Dialog open={bdDispatch.open} onOpenChange={(v) => !v && setBdDispatch({ open: false, reqId: null, statusType: null, paid: "free", mode: "hand", awb: "" })}>
        <DialogContent className="max-w-md" data-testid="bd-dispatch-dialog">
          <DialogHeader>
            <DialogTitle>Yardage Sent</DialogTitle>
            <DialogDescription>Capture dispatch details before marking the status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Sample Type</Label>
              <div className="flex gap-4 text-sm">
                {[{ v: "free", label: "Free" }, { v: "paid", label: "Paid" }].map(opt => (
                  <label key={opt.v} className="flex items-center gap-2 cursor-pointer" data-testid={`bd-paid-${opt.v}`}>
                    <input
                      type="radio"
                      name="bd-paid"
                      value={opt.v}
                      checked={bdDispatch.paid === opt.v}
                      onChange={() => setBdDispatch(s => ({ ...s, paid: opt.v }))}
                      className="text-blue-600"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Sent By</Label>
              <div className="flex gap-4 text-sm">
                {[{ v: "hand", label: "By Hand" }, { v: "courier", label: "By Courier" }].map(opt => (
                  <label key={opt.v} className="flex items-center gap-2 cursor-pointer" data-testid={`bd-mode-${opt.v}`}>
                    <input
                      type="radio"
                      name="bd-mode"
                      value={opt.v}
                      checked={bdDispatch.mode === opt.v}
                      onChange={() => setBdDispatch(s => ({ ...s, mode: opt.v, awb: opt.v === "hand" ? "" : s.awb }))}
                      className="text-blue-600"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {bdDispatch.mode === "courier" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium" htmlFor="bd-awb">AWB Number <span className="text-red-500">*</span></Label>
                <Input
                  id="bd-awb"
                  value={bdDispatch.awb}
                  onChange={(e) => setBdDispatch(s => ({ ...s, awb: e.target.value }))}
                  placeholder="Enter courier AWB number"
                  data-testid="bd-awb-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBdDispatch({ open: false, reqId: null, statusType: null, paid: "free", mode: "hand", awb: "" })}>Cancel</Button>
            <Button onClick={submitBangladeshDispatch} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bd-dispatch-save">Save Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super-admin: Edit order dialog */}
      <Dialog open={!!orderEditing} onOpenChange={(open) => !open && setOrderEditing(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="lead-order-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit order</DialogTitle>
            <DialogDescription>
              Super-admin only. Every change is logged to the audit trail.
            </DialogDescription>
          </DialogHeader>
          {orderEditing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Order Lead ID</label>
                <input className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded"
                  value={orderEditing.order_lead_id}
                  onChange={(e) => setOrderEditing({ ...orderEditing, order_lead_id: e.target.value })}
                  data-testid="lead-edit-order-lead-id" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Fabric Type</label>
                <input className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded"
                  value={orderEditing.fabric_type}
                  onChange={(e) => setOrderEditing({ ...orderEditing, fabric_type: e.target.value })}
                  data-testid="lead-edit-fabric-type" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Composition</label>
                <input className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded"
                  value={orderEditing.composition}
                  onChange={(e) => setOrderEditing({ ...orderEditing, composition: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Order Date</label>
                <input type="date" className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded"
                  value={orderEditing.order_date}
                  onChange={(e) => setOrderEditing({ ...orderEditing, order_date: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Quantity</label>
                <input className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded"
                  value={orderEditing.quantity}
                  onChange={(e) => setOrderEditing({ ...orderEditing, quantity: e.target.value })}
                  placeholder="e.g. 1250" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Order Status</label>
                <select className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded bg-white"
                  value={orderEditing.order_status}
                  onChange={(e) => setOrderEditing({ ...orderEditing, order_status: e.target.value })}
                  data-testid="lead-edit-order-status"
                >
                  <option value="">— None —</option>
                  {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-slate-600">Invoice Amount</label>
                <input className="w-full mt-1 px-3 py-1.5 text-sm border border-slate-200 rounded"
                  value={orderEditing.invoice_amount}
                  onChange={(e) => setOrderEditing({ ...orderEditing, invoice_amount: e.target.value })}
                  placeholder="e.g. 187500" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOrderEditing(null)} disabled={isOrderSaving}>Cancel</Button>
            <Button onClick={saveOrderEdit} disabled={isOrderSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="lead-edit-save-btn">
              {isOrderSaving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Super-admin: Delete order confirmation */}
      <AlertDialog open={!!orderDeleting} onOpenChange={(open) => !open && setOrderDeleting(null)}>
        <AlertDialogContent data-testid="lead-order-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes <strong className="font-mono text-slate-800">{orderDeleting?.order_lead_id}</strong> from this lead's order history. Logged to the audit trail. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isOrderSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmOrderDelete} disabled={isOrderSaving} className="bg-rose-600 hover:bg-rose-700" data-testid="lead-delete-confirm-btn">
              {isOrderSaving ? "Deleting…" : "Delete order"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Raise Data Ticket dialog — agents only. Emails reviewers. */}
      <Dialog open={showBlockDialog} onOpenChange={(v) => !isBlocking && setShowBlockDialog(v)}>
        <DialogContent className="max-w-md" data-testid="block-lead-dialog">
          <DialogHeader>
            <DialogTitle>Block this lead</DialogTitle>
            <DialogDescription>
              The lead stays in the CRM with a Blocked pill. It gets unassigned from the current agent
              and won't be re-allocated until you unblock it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason *</Label>
            <Textarea
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              placeholder="e.g. Client is on hold for 6 months / duplicate lead / not our ICP"
              rows={4}
              data-testid="block-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isBlocking} onClick={() => setShowBlockDialog(false)}>Cancel</Button>
            <Button
              onClick={handleBlockLead}
              disabled={isBlocking || !blockReason.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="block-lead-confirm"
            >
              {isBlocking ? "Blocking…" : "Block lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={ticketOpen} onOpenChange={(v) => !isRaisingTicket && setTicketOpen(v)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />Raise Data Ticket
            </DialogTitle>
            <DialogDescription>
              Flag an issue with this lead's data. The reviewers at kaushlendra.singh@locofast.com and mail@locofast.com will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Reason <span className="text-red-500">*</span></Label>
              <Select value={ticketReason} onValueChange={setTicketReason}>
                <SelectTrigger data-testid="ticket-reason-select">
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Number incorrect">Number incorrect</SelectItem>
                  <SelectItem value="Knits factory">Knits factory</SelectItem>
                  <SelectItem value="Not a garment factory">Not a garment factory</SelectItem>
                  <SelectItem value="Not keen to work with our brand">Not keen to work with our brand</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Comment {ticketReason === "Other" && <span className="text-red-500">*</span>}
                <span className="text-xs text-slate-400 font-normal ml-1">(optional{ticketReason === "Other" ? ", required for Other" : ""})</span>
              </Label>
              <textarea
                value={ticketComment}
                onChange={(e) => setTicketComment(e.target.value)}
                rows={4}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="Add any context that will help the reviewer…"
                data-testid="ticket-comment-textarea"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={isRaisingTicket} onClick={() => setTicketOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                isRaisingTicket ||
                !ticketReason ||
                (ticketReason === "Other" && !ticketComment.trim())
              }
              onClick={async () => {
                setIsRaisingTicket(true);
                try {
                  const res = await axios.post(`${API}/leads/${id}/raise-ticket`, {
                    reason: ticketReason,
                    comment: ticketComment.trim(),
                  });
                  toast.success(res.data.message || "Ticket raised");
                  setTicketOpen(false);
                  setTicketReason("");
                  setTicketComment("");
                  // Refresh tickets so the new open ticket appears in the panel
                  try {
                    const t = await axios.get(`${API}/leads/${id}/tickets`);
                    setLeadTickets(t.data.tickets || []);
                    setLead(prev => prev ? { ...prev, has_open_ticket: true } : prev);
                  } catch {}
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to raise ticket");
                } finally {
                  setIsRaisingTicket(false);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="ticket-submit-btn"
            >
              {isRaisingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertCircle className="w-4 h-4 mr-2" />}
              Raise Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadDetail;
